import { api } from './api.js';
import { esc, showToast, hasPerm, fmtDate, fmtDateTime, relativeTime, initPasswordToggles, resetPasswordInput, applyNavAccess } from './utils.js';
import { initPageChrome } from './page-chrome.js';
import { toggleTheme } from './theme-ui.js';
import { installSessionHandler } from './session.js';
import { TERMINAL_FLOWS, findTerminalFlow, listCatalogSteps } from './terminal-flow-catalog.js';
import { overrideStorageKey, setTerminalFlowOverrides } from './terminal-flow-merge.js';
import {
  setTerminalFlowValidationRules,
  resolveValidationRule,
  describeValidationRule,
  listFlowValidationSummary,
  sanitizeValidationOverride,
  VALIDATION_RULE_TYPES,
  validationStorageKey,
  inferValidationRule,
} from './terminal-flow-validation.js';
import {
  createSimulatorSession,
  getSimulatorQuestion,
  submitSimulatorAnswer,
  resetSimulatorSession,
  formatSimulatorPreview,
  getSimulatorComparePreview,
  formatComparePreview,
  suggestSimulatorAnswer,
  listSimulatorMockPresets,
  runSimulatorHappyPath,
  getSimulatorWordingCompare,
  formatWordingCompareText,
} from './terminal-flow-simulator.js';
import { checkTerminalFlowDrift, findStaleOverrides, removeStaleOverrides } from './terminal-flow-drift.js';
import {
  setTerminalFlowCustomSteps,
  getCustomStepsForFlow,
  validateCustomStepConfig,
  sanitizeCustomStep,
  assertCustomStepLimit,
  findCustomStepById,
  findStaleCustomSteps,
  removeStaleCustomSteps,
  moveCustomStepInFlow,
  countAllStaleFlowConfig,
} from './terminal-flow-custom.js';
import { listHandlerTemplates } from './terminal-flow-handlers.js';
import {
  parseFlowConfigImport,
  diffCustomStepsMaps,
  formatCustomStepsDiffHtml,
  formatFlowConfigImportSummary,
  mergeCustomStepsMaps,
  downloadCustomStepsJson,
  downloadFlowConfigJson,
} from './terminal-flow-custom-io.js';
import {
  downloadOverridesJson,
  parseOverridesImport,
  mergeOverrideMaps,
  filterOverridesForFlow,
  countOverridesForFlow,
  diffOverrideMaps,
  formatOverrideDiffHtml,
} from './terminal-flow-overrides-io.js';

const PERM_KEYS = [
  ['view_all', 'View all leads'],
  ['edit_all', 'Edit all leads'],
  ['takeover', 'Take over inquiries'],
  ['delete', 'Delete / recycle'],
  ['export', 'Export CSV'],
  ['import', 'Import data'],
  ['manage_agents', 'Manage agents'],
  ['manage_settings', 'Manage settings'],
  ['view_reports', 'View reports'],
  ['view_accounting', 'Ledger tab (Overview)'],
  ['view_only', 'View only (no edits)'],
];

const DEPARTMENTS = ['tour', 'visa', 'ticket'];
const DEPT_LABELS = { tour: 'Tours', visa: 'Visa', ticket: 'Tickets' };

const DEFAULT_PIPELINES = {
  visa: ['new', 'details_provided', 'visa_applied', 'visa_issued'],
  tour: [
    'new', 'quotation', 'tour_accepted_advance', 'tour_accepted_progress', 'tour_finalised',
  ],
  ticket: ['new', 'quotation_sent', 'temporary_booking', 'booking_confirmed', 'ticket_issued'],
};

const TAB_PERMS = {
  overview: () => true,
  agents: (u) => u.role === 'admin' || hasPerm(u, 'manage_agents'),
  targets: (u) => u.role === 'admin' || hasPerm(u, 'manage_agents'),
  lists: (u) => u.role === 'admin' || hasPerm(u, 'manage_settings'),
  'assistant-kb': (u) => u.role === 'admin' || hasPerm(u, 'manage_settings'),
  'terminal-flows': (u) => u.role === 'admin' || hasPerm(u, 'manage_settings'),
  pipelines: (u) => u.role === 'admin' || hasPerm(u, 'manage_settings'),
  recycle: (u) => u.role === 'admin' || hasPerm(u, 'delete'),
  audit: (u) => u.role === 'admin' || hasPerm(u, 'manage_agents') || hasPerm(u, 'manage_settings'),
  activity: (u) => u.role === 'admin' || hasPerm(u, 'manage_agents') || hasPerm(u, 'manage_settings'),
  import: (u) => u.role === 'admin' || hasPerm(u, 'import'),
  duplicates: (u) => u.role === 'admin' || hasPerm(u, 'edit_all'),
  password: () => true,
};

let me = null;
let settings = {};
let editingPerms = {};
let auditPage = 1;
let auditTotal = 0;
let activityPage = 1;
let activityTotal = 0;
let activityAgents = [];
let activitySelectedAgent = '';
let kbEntries = [];
let kbEditingId = null;
let tfSelectedFlowId = TERMINAL_FLOWS[0]?.id || '';
let tfSelectedStepKey = '';
let tfMode = 'inspector';
let tfSimSession = null;
let tfStepSearch = '';
let tfSimMockPreset = 'default';
let tfSimHappyPaused = false;
let tfCustomEditingId = null;
let tfPendingImport = null;

const $ = (id) => document.getElementById(id);

function canAccessAdmin() {
  return me && (me.role === 'admin' || hasPerm(me, 'manage_agents') || hasPerm(me, 'manage_settings')
    || hasPerm(me, 'delete') || hasPerm(me, 'import') || hasPerm(me, 'edit_all'));
}

function applyTabVisibility() {
  document.querySelectorAll('[data-tab]').forEach((btn) => {
    const tab = btn.dataset.tab;
    const allowed = TAB_PERMS[tab] ? TAB_PERMS[tab](me) : true;
    btn.classList.toggle('hidden', !allowed);
  });
  const firstVisible = document.querySelector('[data-tab]:not(.hidden)');
  if (firstVisible) showTab(firstVisible.dataset.tab);
}

function showTab(tab) {
  document.querySelectorAll('[data-tab]').forEach((b) => {
    b.classList.toggle('active', b.dataset.tab === tab && !b.classList.contains('hidden'));
  });
  Object.keys(TAB_PERMS).forEach((t) => {
    $('tab-' + t)?.classList.toggle('hidden', t !== tab);
  });
  if (tab === 'recycle') loadRecycle();
  if (tab === 'audit') loadAudit();
  if (tab === 'activity') loadActivityTab();
  if (tab === 'duplicates') loadDuplicateScan();
  if (tab === 'targets') loadTargets();
  if (tab === 'pipelines') renderPipelines();
  if (tab === 'assistant-kb') loadAssistantKb();
  if (tab === 'terminal-flows') loadTerminalFlowsTab();
}

function showAdminShell() {
  $('loginGate')?.classList.add('hidden');
  const shell = $('adminShell');
  if (!shell) return;
  shell.classList.remove('hidden');
  shell.classList.add('visible');
}

function showLoginGate(forbidden = false) {
  const shell = $('adminShell');
  if (shell) {
    shell.classList.add('hidden');
    shell.classList.remove('visible');
  }
  const gate = $('loginGate');
  gate?.classList.remove('hidden');
  if (forbidden) {
    $('loginGateTitle').textContent = 'You do not have admin access';
    $('loginGateHint').textContent = `${me?.name || 'Your account'} cannot open the admin panel. Contact an admin if you need access.`;
    $('loginGateBtn').textContent = 'Back to Home';
    $('loginGateBtn').href = '/';
  }
}

function hideAdminViews() {
  $('loginGate')?.classList.add('hidden');
  const shell = $('adminShell');
  if (shell) {
    shell.classList.add('hidden');
    shell.classList.remove('visible');
  }
}

async function boot() {
  installSessionHandler({
    message: 'Session expired — please sign in again',
    on401() {
      showLoginGate(false);
    },
  });
  initPasswordToggles();
  hideAdminViews();
  try {
    const data = await api('/api/me');
    me = data.agent;
    if (!canAccessAdmin()) {
      showLoginGate(true);
      return;
    }
    if (me.must_change_password) showForcePasswordModal();
    showAdminShell();
    $('agentName').textContent = me.name;
    $('agentDot').style.background = me.color || '#e8a838';
    initPageChrome(me);
    applyNavAccess(me);
    document.getElementById('themeToggle')?.addEventListener('click', toggleTheme);
    $('logoutBtn')?.addEventListener('click', logoutAdmin);
    $('fpSaveBtn')?.addEventListener('click', saveForcedPassword);
    applyTabVisibility();
    renderOverviewAccess();
    await loadAll();
    bindTabs();
    bindActions();
  } catch (e) {
    showLoginGate(false);
  }
}

async function logoutAdmin() {
  try { await api('/api/logout', { method: 'POST' }); } catch { /* ignore */ }
  location.href = '/';
}

function showForcePasswordModal() {
  const m = $('forcePassModal');
  if (!m) return;
  m.style.display = '';
  m.classList.add('open');
}

function hideForcePasswordModal() {
  const m = $('forcePassModal');
  if (!m) return;
  m.style.display = 'none';
  m.classList.remove('open');
}

async function saveForcedPassword() {
  const next = $('fpNew')?.value || '';
  const confirm = $('fpConfirm')?.value || '';
  if (next.length < 8) {
    showToast('Password must be at least 8 characters', 'warning');
    return;
  }
  if (next !== confirm) {
    showToast('Passwords do not match', 'warning');
    return;
  }
  try {
    await api('/api/me/password', { method: 'POST', body: { new_password: next } });
    me.must_change_password = false;
    hideForcePasswordModal();
    resetPasswordInput($('fpNew'));
    resetPasswordInput($('fpConfirm'));
    showToast('Password updated', 'success');
  } catch (ex) {
    showToast(ex.message, 'error');
  }
}

function renderOverviewAccess() {
  const canSettings = TAB_PERMS.lists(me);
  $('adminGeneralSettingsCard')?.classList.toggle('hidden', !canSettings);
  const canExport = me.role === 'admin' || hasPerm(me, 'export');
  $('adminExportCard')?.classList.toggle('hidden', !canExport);
}

function bindTabs() {
  document.querySelectorAll('[data-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (btn.classList.contains('hidden')) return;
      showTab(btn.dataset.tab);
    });
  });
}

function bindActions() {
  $('createAgentBtn')?.addEventListener('click', createAgent);
  document.querySelectorAll('[data-new-dept]').forEach((cb) => {
    cb.addEventListener('change', () => syncPrimaryDeptOptions('new'));
  });
  document.querySelectorAll('[data-edit-dept]').forEach((cb) => {
    cb.addEventListener('change', () => syncPrimaryDeptOptions('edit'));
  });
  $('addTourBtn')?.addEventListener('click', () => addListItem('tours', 'newTour'));
  $('addVisaBtn')?.addEventListener('click', () => addVisaWithCost());
  $('addSourceBtn')?.addEventListener('click', () => addListItem('sources', 'newSource'));
  $('addAirlineBtn')?.addEventListener('click', () => addListItem('airlines', 'newAirline'));
  $('dryRunBtn')?.addEventListener('click', () => runImport(true));
  $('importBtn')?.addEventListener('click', () => runImport(false));
  $('importFile')?.addEventListener('change', async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    $('importJson').value = await file.text();
  });
  $('changePassBtn')?.addEventListener('click', changePassword);
  $('closeEditAgent')?.addEventListener('click', () => $('editAgentModal').classList.remove('open'));
  $('cancelEditAgent')?.addEventListener('click', () => $('editAgentModal').classList.remove('open'));
  $('saveEditAgent')?.addEventListener('click', saveAgentEdit);
  $('scanDupBtn')?.addEventListener('click', loadDuplicateScan);
  $('loadTargetsBtn')?.addEventListener('click', loadTargets);
  $('saveGeneralSettingsBtn')?.addEventListener('click', saveGeneralSettings);
  $('savePipelinesBtn')?.addEventListener('click', savePipelines);
  $('resetPipelinesBtn')?.addEventListener('click', resetPipelines);
  $('kbSaveBtn')?.addEventListener('click', saveAssistantKbEntry);
  $('kbCancelBtn')?.addEventListener('click', resetAssistantKbForm);
  $('auditLoadMoreBtn')?.addEventListener('click', () => {
    auditPage += 1;
    loadAudit(true);
  });
  $('activityApplyBtn')?.addEventListener('click', () => {
    activityPage = 1;
    loadActivity();
  });
  $('activityLoadMoreBtn')?.addEventListener('click', () => {
    activityPage += 1;
    loadActivity(true);
  });
  $('activityAgentFilter')?.addEventListener('change', () => {
    activitySelectedAgent = $('activityAgentFilter').value;
    highlightActivityAgentCard();
  });
  $('activityPurgePreviewBtn')?.addEventListener('click', previewActivityPurge);
  $('activityPurgeBtn')?.addEventListener('click', runActivityPurge);
  document.querySelectorAll('[data-purge-days]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const days = Number(btn.dataset.purgeDays);
      const d = new Date(Date.now() - days * 864e5);
      $('activityPurgeBefore').value = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Colombo' });
      previewActivityPurge();
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && $('editAgentModal')?.classList.contains('open')) {
      $('editAgentModal').classList.remove('open');
    }
  });
}

async function loadAll() {
  const settingsRes = await api('/api/settings');
  settings = settingsRes.settings || {};
  renderGeneralSettings();
  if (TAB_PERMS.agents(me)) {
    const agentsRes = await api('/api/agents?all=1');
    renderAgents(agentsRes.agents || []);
  } else if ($('agentsList')) {
    $('agentsList').innerHTML = '<p style="color:var(--fg-dim);font-size:13px">You do not have permission to manage agents.</p>';
  }
  renderLists();
  if (TAB_PERMS.pipelines(me)) renderPipelines();
}

function resetAssistantKbForm() {
  kbEditingId = null;
  if ($('kbTitle')) $('kbTitle').value = '';
  if ($('kbCategory')) $('kbCategory').value = 'general';
  if ($('kbSort')) $('kbSort').value = '50';
  if ($('kbActive')) $('kbActive').checked = true;
  if ($('kbTriggers')) $('kbTriggers').value = '';
  if ($('kbAnswer')) $('kbAnswer').value = '';
  $('kbSaveBtn').textContent = 'Save entry';
  $('kbCancelBtn')?.classList.add('hidden');
}

function fillAssistantKbForm(entry) {
  kbEditingId = entry.id;
  $('kbTitle').value = entry.title || '';
  $('kbCategory').value = entry.category || 'general';
  $('kbSort').value = String(entry.sort_order ?? 0);
  $('kbActive').checked = entry.active !== false;
  $('kbTriggers').value = (entry.triggers || []).join('\n');
  $('kbAnswer').value = entry.answer || '';
  $('kbSaveBtn').textContent = 'Update entry';
  $('kbCancelBtn')?.classList.remove('hidden');
}

async function loadAssistantKb() {
  if (!TAB_PERMS['assistant-kb'](me)) {
    if ($('kbEntriesList')) {
      $('kbEntriesList').innerHTML = '<p class="admin-muted">You do not have permission to edit the assistant knowledge base.</p>';
    }
    return;
  }
  try {
    const res = await api('/api/assistant/knowledge?all=1');
    kbEntries = res.entries || [];
    renderAssistantKbList();
  } catch (e) {
    showToast(e.message || 'Could not load knowledge base', 'error');
  }
}

function renderAssistantKbList() {
  const el = $('kbEntriesList');
  if (!el) return;
  if (!kbEntries.length) {
    el.innerHTML = '<p class="admin-muted">No entries yet. Add one above.</p>';
    return;
  }
  el.innerHTML = kbEntries.map((entry) => {
    const triggers = (entry.triggers || []).slice(0, 3).join(' · ');
    const more = (entry.triggers || []).length > 3 ? ` +${entry.triggers.length - 3}` : '';
    return `
      <div class="agent-row" style="align-items:flex-start">
        <div style="flex:1;min-width:0">
          <div style="font-weight:600">${esc(entry.title)} <span style="font-size:11px;color:var(--fg-dim)">${esc(entry.category)} · sort ${entry.sort_order ?? 0}${entry.active ? '' : ' · inactive'}</span></div>
          <div style="font-size:11px;color:var(--fg-muted);margin-top:4px">Triggers: ${esc(triggers)}${esc(more)}</div>
          <div style="font-size:12px;color:var(--fg-dim);margin-top:6px;white-space:pre-wrap">${esc(String(entry.answer || '').slice(0, 160))}${(entry.answer || '').length > 160 ? '…' : ''}</div>
        </div>
        <div style="display:flex;gap:6px;flex-shrink:0">
          <button class="btn btn-ghost btn-sm" type="button" data-kb-edit="${esc(entry.id)}">Edit</button>
          <button class="btn btn-ghost btn-sm" type="button" data-kb-del="${esc(entry.id)}">Delete</button>
        </div>
      </div>`;
  }).join('');

  el.querySelectorAll('[data-kb-edit]').forEach((btn) => {
    btn.onclick = () => {
      const entry = kbEntries.find((x) => x.id === btn.dataset.kbEdit);
      if (entry) fillAssistantKbForm(entry);
    };
  });
  el.querySelectorAll('[data-kb-del]').forEach((btn) => {
    btn.onclick = async () => {
      const entry = kbEntries.find((x) => x.id === btn.dataset.kbDel);
      if (!entry || !confirm(`Delete "${entry.title}"?`)) return;
      try {
        await api(`/api/assistant/knowledge/${encodeURIComponent(entry.id)}`, { method: 'DELETE' });
        showToast('Entry deleted', 'success');
        if (kbEditingId === entry.id) resetAssistantKbForm();
        await loadAssistantKb();
      } catch (e) {
        showToast(e.message, 'error');
      }
    };
  });
}

async function saveAssistantKbEntry() {
  if (!TAB_PERMS['assistant-kb'](me)) return;
  const body = {
    title: $('kbTitle')?.value?.trim(),
    category: $('kbCategory')?.value || 'general',
    sort_order: Number($('kbSort')?.value || 0),
    active: !!$('kbActive')?.checked,
    triggers: ($('kbTriggers')?.value || '').split('\n').map((t) => t.trim()).filter(Boolean),
    answer: $('kbAnswer')?.value || '',
  };
  try {
    if (kbEditingId) {
      await api(`/api/assistant/knowledge/${encodeURIComponent(kbEditingId)}`, { method: 'PUT', body });
      showToast('Entry updated', 'success');
    } else {
      await api('/api/assistant/knowledge', { method: 'POST', body });
      showToast('Entry added', 'success');
    }
    resetAssistantKbForm();
    await loadAssistantKb();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

function getTerminalFlowOverridesMap() {
  const raw = settings.terminal_flow_overrides;
  return raw && typeof raw === 'object' ? raw : {};
}

function getTfOverride(flowId, stepKey) {
  return getTerminalFlowOverridesMap()[overrideStorageKey(flowId, stepKey)] || null;
}

async function saveTerminalFlowOverrides(next) {
  await api('/api/settings', { method: 'PUT', body: { terminal_flow_overrides: next } });
  settings.terminal_flow_overrides = next;
  setTerminalFlowOverrides(next);
}

function getTerminalFlowCustomStepsMap() {
  const raw = settings.terminal_flow_custom_steps;
  return raw && typeof raw === 'object' ? raw : {};
}

async function saveTerminalFlowCustomSteps(next) {
  await api('/api/settings', { method: 'PUT', body: { terminal_flow_custom_steps: next } });
  settings.terminal_flow_custom_steps = next;
  setTerminalFlowCustomSteps(next);
}

function getTerminalFlowValidationRulesMap() {
  const raw = settings.terminal_flow_validation_rules;
  return raw && typeof raw === 'object' ? raw : {};
}

function getTfValidationOverride(flowId, stepKey) {
  return getTerminalFlowValidationRulesMap()[validationStorageKey(flowId, stepKey)] || null;
}

async function saveTerminalFlowValidationRules(next) {
  await api('/api/settings', { method: 'PUT', body: { terminal_flow_validation_rules: next } });
  settings.terminal_flow_validation_rules = next;
  setTerminalFlowValidationRules(next);
}

function readTfValidationPatchFromForm(rule) {
  if (!rule?.editable) return null;
  const form = { params: {} };
  if ($('tfValRequired')) form.required = !!$('tfValRequired').checked;
  (VALIDATION_RULE_TYPES[rule.type]?.params || []).forEach((p) => {
    const el = $(`tfValParam_${p.key}`);
    if (!el) return;
    form.params[p.key] = p.type === 'boolean' ? el.checked : el.value;
  });
  return sanitizeValidationOverride(form, rule);
}

function buildTfValidationEditorHtml(flow, step, rule, override) {
  const spec = VALIDATION_RULE_TYPES[rule.type] || VALIDATION_RULE_TYPES.locked;
  const storageKey = validationStorageKey(flow.id, step.key);
  const customized = !!override || rule.hasOverride;
  if (!rule.editable) {
    return `
    <div class="form-group" style="margin-top:14px;padding-top:14px;border-top:1px solid var(--glass-border-soft)">
      <h4 style="margin:0 0 8px;font-size:13px">Validation</h4>
      <p class="admin-muted" style="font-size:12px;margin:0 0 6px"><strong>${esc(spec.label)}</strong> · ${esc(describeValidationRule(rule))}</p>
      <p class="admin-muted" style="font-size:11px;color:var(--orange)">${esc(rule.lockedReason || 'This step uses complex logic in code — view only.')}</p>
    </div>`;
  }
  const paramFields = (spec.params || []).map((p) => {
    const val = override?.params?.[p.key] ?? rule.params?.[p.key] ?? p.default ?? '';
    if (p.type === 'boolean') {
      return `<label class="tf-sim-opt" style="display:flex;align-items:center;gap:6px;font-size:12px;margin-bottom:8px">
        <input type="checkbox" id="tfValParam_${esc(p.key)}"${val ? ' checked' : ''}>
        ${esc(p.label)}
      </label>`;
    }
    return `<div class="form-group">
      <label class="form-label" for="tfValParam_${esc(p.key)}">${esc(p.label)}</label>
      <input class="form-input" id="tfValParam_${esc(p.key)}" type="number" value="${esc(val)}" min="${p.min ?? ''}" max="${p.max ?? ''}">
    </div>`;
  }).join('');
  return `
  <div class="form-group" style="margin-top:14px;padding-top:14px;border-top:1px solid var(--glass-border-soft)">
    <h4 style="margin:0 0 8px;font-size:13px">Validation rules${customized ? ' <span style="color:var(--accent);font-size:11px">customized</span>' : ''}</h4>
    <p class="admin-muted" style="font-size:11px;margin:0 0 10px">Type: <strong>${esc(spec.label)}</strong> · ${esc(spec.description || '')}</p>
    <label class="tf-sim-opt" style="display:flex;align-items:center;gap:6px;font-size:12px;margin-bottom:10px">
      <input type="checkbox" id="tfValRequired"${rule.required ? ' checked' : ''}>
      Required (reject skip / empty)
    </label>
    ${paramFields}
    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px">
      <button class="btn btn-primary btn-sm" type="button" id="tfValSaveBtn">Save validation</button>
      <button class="btn btn-ghost btn-sm" type="button" id="tfValResetBtn">Reset validation</button>
    </div>
    <p class="admin-muted" style="margin-top:8px;font-size:11px">Default: ${esc(describeValidationRule(inferValidationRule(step.key, step)))} · Storage: <code>${esc(storageKey)}</code></p>
  </div>`;
}

function renderTfValidationPanel() {
  const el = $('tfValidationTable');
  if (!el) return;
  const flow = findTerminalFlow(tfSelectedFlowId);
  if (!flow) {
    el.innerHTML = '<p class="admin-muted">Pick a flow above.</p>';
    return;
  }
  const rows = listFlowValidationSummary(flow.id, listCatalogSteps(flow));
  const q = tfStepSearch.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    if (!q) return true;
    const hay = [r.key, r.label, r.description].join(' ').toLowerCase();
    return hay.includes(q);
  });
  if (!filtered.length) {
    el.innerHTML = '<p class="admin-muted">No steps match your search.</p>';
    return;
  }
  el.innerHTML = `<table class="admin-table" style="width:100%;font-size:12px">
    <thead><tr><th>Step</th><th>Rule</th><th>Details</th><th></th></tr></thead>
    <tbody>${filtered.map((r) => `<tr>
      <td><code style="font-size:10px">${esc(r.key)}</code><br>${esc(r.label)}</td>
      <td>${esc(VALIDATION_RULE_TYPES[r.rule.type]?.label || r.rule.type)}${r.customized ? ' <span style="color:var(--accent)">*</span>' : ''}</td>
      <td class="admin-muted">${esc(r.description)}</td>
      <td><button type="button" class="btn btn-ghost btn-sm" data-tf-val-edit="${esc(r.key)}">Edit</button></td>
    </tr>`).join('')}</tbody>
  </table>
  <p class="admin-muted" style="margin-top:8px;font-size:11px">* customized from default · Locked rules are view-only in Inspector</p>`;
  el.querySelectorAll('[data-tf-val-edit]').forEach((btn) => {
    btn.onclick = () => {
      tfSelectedStepKey = btn.dataset.tfValEdit;
      setTfMode('inspector');
      renderTfStepsList();
      renderTfStepDetail();
    };
  });
}

function resetTfCustomForm() {
  tfCustomEditingId = null;
  $('tfCustomFormTitle').textContent = 'Add custom step';
  $('tfCustomSaveBtn').textContent = 'Add custom step';
  $('tfCustomCancelBtn')?.classList.add('hidden');
  $('tfCustomKey')?.removeAttribute('readonly');
  if ($('tfCustomAsk')) $('tfCustomAsk').value = '';
  if ($('tfCustomHint')) $('tfCustomHint').value = '';
  if ($('tfCustomKey')) $('tfCustomKey').value = '';
  if ($('tfCustomChoices')) $('tfCustomChoices').value = '';
  if ($('tfCustomAfter')) $('tfCustomAfter').value = '__end__';
  const handler = listHandlerTemplates()[0];
  if ($('tfCustomHandler') && handler) {
    $('tfCustomHandler').value = handler.id;
    $('tfCustomChoicesWrap')?.classList.toggle('hidden', !handler.needsChoices);
  }
  if ($('tfCustomOptional')) $('tfCustomOptional').checked = handler?.defaultOptional !== false;
}

function loadTfCustomFormForEdit(step) {
  tfCustomEditingId = step.id;
  $('tfCustomFormTitle').textContent = 'Edit custom step';
  $('tfCustomSaveBtn').textContent = 'Save changes';
  $('tfCustomCancelBtn')?.classList.remove('hidden');
  $('tfCustomHandler').value = step.handler;
  $('tfCustomKey').value = step.stepKey;
  $('tfCustomKey')?.setAttribute('readonly', 'readonly');
  $('tfCustomAsk').value = step.ask;
  $('tfCustomHint').value = step.hint || '';
  $('tfCustomAfter').value = step.afterStep || '__end__';
  $('tfCustomChoices').value = step.choices || '';
  $('tfCustomOptional').checked = step.optional != null ? step.optional : listHandlerTemplates().find((h) => h.id === step.handler)?.defaultOptional !== false;
  const handler = listHandlerTemplates().find((h) => h.id === step.handler);
  $('tfCustomChoicesWrap')?.classList.toggle('hidden', !handler?.needsChoices);
  $('tfAdvancedPanel')?.scrollIntoView({ block: 'nearest' });
}

function renderTfCustomHandlerSelect() {
  const select = $('tfCustomHandler');
  if (!select || select.dataset.tfBound) return;
  select.dataset.tfBound = '1';
  select.innerHTML = listHandlerTemplates().map((h) =>
    `<option value="${esc(h.id)}">${esc(h.label)}</option>`,
  ).join('');
  select.addEventListener('change', () => {
    const handler = listHandlerTemplates().find((h) => h.id === select.value);
    const wrap = $('tfCustomChoicesWrap');
    if (wrap) wrap.classList.toggle('hidden', !handler?.needsChoices);
    const opt = $('tfCustomOptional');
    if (opt && handler) opt.checked = handler.defaultOptional !== false;
  });
}

function renderTfCustomAfterSelect() {
  const select = $('tfCustomAfter');
  if (!select) return;
  const flow = findTerminalFlow(tfSelectedFlowId);
  const steps = flow ? listCatalogSteps(flow).filter((s) => !s.pattern && !s.custom) : [];
  select.innerHTML = [
    '<option value="__end__">End of flow</option>',
    '<option value="__start__">Start of flow</option>',
    ...steps.map((s) => `<option value="${esc(s.key)}">${esc(s.label || s.key)}</option>`),
  ].join('');
}

function renderTfCustomStepsPanel() {
  renderTfCustomHandlerSelect();
  renderTfCustomAfterSelect();
  const list = $('tfCustomStepsList');
  if (!list) return;
  const steps = getCustomStepsForFlow(tfSelectedFlowId);
  if (!steps.length) {
    list.innerHTML = '<p class="admin-muted">No custom steps for this flow.</p>';
    return;
  }
  const handlers = Object.fromEntries(listHandlerTemplates().map((h) => [h.id, h.label]));
  list.innerHTML = steps.map((s) => `
    <div class="tf-custom-row" style="display:flex;justify-content:space-between;gap:8px;align-items:flex-start;padding:8px 0;border-bottom:1px solid var(--glass-border-soft)">
      <div style="font-size:12px">
        <strong>${esc(s.ask)}</strong>
        <div style="color:var(--fg-dim);margin-top:2px"><code>${esc(s.stepKey)}</code> · ${esc(handlers[s.handler] || s.handler)} · after <code>${esc(s.afterStep || '__end__')}</code></div>
      </div>
      <div style="display:flex;gap:6px;flex-shrink:0">
        <button class="btn btn-ghost btn-sm" type="button" data-tf-custom-up="${esc(s.id)}" title="Move earlier (same anchor first)">▲</button>
        <button class="btn btn-ghost btn-sm" type="button" data-tf-custom-down="${esc(s.id)}" title="Move later (same anchor first)">▼</button>
        <button class="btn btn-ghost btn-sm" type="button" data-tf-custom-edit="${esc(s.id)}">Edit</button>
        <button class="btn btn-ghost btn-sm" type="button" data-tf-custom-del="${esc(s.id)}">Remove</button>
      </div>
    </div>`).join('');
  list.querySelectorAll('[data-tf-custom-edit]').forEach((btn) => {
    btn.onclick = () => {
      const step = findCustomStepById(tfSelectedFlowId, btn.dataset.tfCustomEdit);
      if (step) loadTfCustomFormForEdit(step);
    };
  });
  list.querySelectorAll('[data-tf-custom-up]').forEach((btn) => {
    btn.onclick = () => moveTfCustomStep(btn.dataset.tfCustomUp, -1);
  });
  list.querySelectorAll('[data-tf-custom-down]').forEach((btn) => {
    btn.onclick = () => moveTfCustomStep(btn.dataset.tfCustomDown, 1);
  });
  list.querySelectorAll('[data-tf-custom-del]').forEach((btn) => {
    btn.onclick = async () => {
      if (!confirm('Remove this custom step?')) return;
      const map = { ...getTerminalFlowCustomStepsMap() };
      map[tfSelectedFlowId] = (map[tfSelectedFlowId] || []).filter((s) => s.id !== btn.dataset.tfCustomDel);
      if (!map[tfSelectedFlowId]?.length) delete map[tfSelectedFlowId];
      try {
        await saveTerminalFlowCustomSteps(map);
        showToast('Custom step removed', 'success');
        renderTfCustomStepsPanel();
        renderTfStaleCustomSteps();
        renderTfStepsList();
        if (tfMode === 'simulator') restartTfSimulator();
      } catch (e) {
        showToast(e.message, 'error');
      }
    };
  });
}

async function moveTfCustomStep(stepId, delta) {
  const map = getTerminalFlowCustomStepsMap();
  const { next, moved, afterStep, reordered } = moveCustomStepInFlow(map, tfSelectedFlowId, stepId, delta);
  if (!moved) return;
  try {
    await saveTerminalFlowCustomSteps(next);
    showToast(reordered ? `Reordered within ${afterStep}` : `Moved after ${afterStep}`, 'success');
    renderTfCustomStepsPanel();
    renderTfStaleCustomSteps();
    renderTfStepsList();
    if (tfMode === 'simulator') restartTfSimulator();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

async function cleanAllTfStale() {
  const overrides = getTerminalFlowOverridesMap();
  const custom = getTerminalFlowCustomStepsMap();
  const total = countAllStaleFlowConfig(overrides, custom);
  if (!total) {
    showToast('No stale overrides or custom steps', 'success');
    return;
  }
  if (!confirm(`Remove ${total} stale item(s) (overrides + custom steps)?`)) return;
  const { next: overridesNext, removed: removedOverrides } = removeStaleOverrides(overrides);
  const { next: customNext, removed: removedCustom } = removeStaleCustomSteps(custom);
  try {
    if (removedOverrides.length) await saveTerminalFlowOverrides(overridesNext);
    if (removedCustom) await saveTerminalFlowCustomSteps(customNext);
    showToast(`Removed ${removedOverrides.length} override(s) and ${removedCustom} custom step(s)`, 'success');
    renderTfStaleOverrides();
    renderTfStaleCustomSteps();
    renderTfDriftBanner();
    renderTfStepsList();
    if (tfMode === 'simulator') restartTfSimulator();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

async function saveTfCustomStep() {
  const handler = $('tfCustomHandler')?.value;
  const stepKey = $('tfCustomKey')?.value?.trim() || '';
  const ask = $('tfCustomAsk')?.value?.trim() || '';
  const hint = $('tfCustomHint')?.value?.trim() || '';
  const afterStep = $('tfCustomAfter')?.value || '__end__';
  const choices = $('tfCustomChoices')?.value || '';
  const optional = !!$('tfCustomOptional')?.checked;
  const existing = tfCustomEditingId ? findCustomStepById(tfSelectedFlowId, tfCustomEditingId) : null;
  const draft = sanitizeCustomStep({
    id: existing?.id,
    handler,
    stepKey: stepKey.startsWith('custom_') ? stepKey : `custom_${stepKey.replace(/^custom_/, '')}`,
    ask,
    hint,
    afterStep,
    choices,
    optional,
  }, tfSelectedFlowId);
  const err = validateCustomStepConfig(draft, tfSelectedFlowId, { excludeStepId: draft.id });
  if (err) {
    showToast(err, 'error');
    return;
  }
  const map = { ...getTerminalFlowCustomStepsMap() };
  const list = [...(map[tfSelectedFlowId] || [])];
  if (tfCustomEditingId) {
    const idx = list.findIndex((s) => s.id === tfCustomEditingId);
    if (idx >= 0) list[idx] = draft;
    else list.push(draft);
  } else {
    const siblings = list.filter((s) => (s.afterStep || '__end__') === afterStep);
    const maxOrder = siblings.reduce((m, s) => Math.max(m, s.sortOrder ?? 0), -1);
    draft.sortOrder = maxOrder + 1;
    list.push(draft);
  }
  const limitErr = assertCustomStepLimit(tfSelectedFlowId, list);
  if (limitErr) {
    showToast(limitErr, 'error');
    return;
  }
  map[tfSelectedFlowId] = list;
  try {
    await saveTerminalFlowCustomSteps(map);
    showToast(tfCustomEditingId ? 'Custom step updated' : 'Custom step added', 'success');
    resetTfCustomForm();
    renderTfCustomStepsPanel();
    renderTfStaleCustomSteps();
    renderTfStepsList();
    if (tfMode === 'simulator') restartTfSimulator();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

function renderTfStaleCustomSteps() {
  const summary = $('tfStaleCustomSummary');
  const list = $('tfStaleCustomList');
  const card = $('tfStaleCustomCard');
  if (!summary || !list) return;
  const stale = findStaleCustomSteps(getTerminalFlowCustomStepsMap());
  if (!stale.length) {
    card?.classList.add('hidden');
    return;
  }
  card?.classList.remove('hidden');
  summary.textContent = `${stale.length} custom step(s) have orphan anchors, invalid handlers, or duplicate keys.`;
  list.innerHTML = `<ul style="margin:8px 0 0;padding-left:18px;font-size:12px">${stale.map((s) =>
    `<li><code>${esc(s.stepKey)}</code> — ${esc(s.message)}</li>`,
  ).join('')}</ul>`;
}

async function cleanStaleTfCustomSteps() {
  const { next, removed } = removeStaleCustomSteps(getTerminalFlowCustomStepsMap());
  if (!removed) {
    showToast('No stale custom steps', 'success');
    return;
  }
  if (!confirm(`Remove ${removed} stale custom step(s)?`)) return;
  try {
    await saveTerminalFlowCustomSteps(next);
    showToast(`Removed ${removed} stale custom step(s)`, 'success');
    resetTfCustomForm();
    renderTfStaleCustomSteps();
    renderTfCustomStepsPanel();
    renderTfStepsList();
    if (tfMode === 'simulator') restartTfSimulator();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

function exportTfCustomSteps() {
  const map = getTerminalFlowCustomStepsMap();
  const count = Object.values(map).reduce((n, list) => n + (list?.length || 0), 0);
  downloadCustomStepsJson(map);
  showToast(`Exported ${count} custom step(s)`, 'success');
}

function exportTfFlowConfig() {
  const overrides = getTerminalFlowOverridesMap();
  const custom = getTerminalFlowCustomStepsMap();
  downloadFlowConfigJson(overrides, custom);
  showToast('Exported overrides + custom steps', 'success');
}

function renderTfStaleOverrides() {
  const summary = $('tfStaleSummary');
  const list = $('tfStaleList');
  const card = $('tfStaleCard');
  if (!summary || !list) return;
  const stale = findStaleOverrides(getTerminalFlowOverridesMap());
  if (!stale.length) {
    card?.classList.add('hidden');
    return;
  }
  card?.classList.remove('hidden');
  summary.textContent = `${stale.length} override key(s) no longer match the catalog. Remove them to avoid confusion.`;
  list.innerHTML = `<ul style="margin:8px 0 0;padding-left:18px;font-size:12px">${stale.map((s) =>
    `<li><code>${esc(s.key)}</code> — ${esc(s.message)}</li>`,
  ).join('')}</ul>`;
}

async function loadTfOverrideHistory() {
  const el = $('tfOverrideHistoryList');
  if (!el) return;
  try {
    const data = await api('/api/audit?page=1&limit=8&action=terminal_flow_overrides.update');
    const events = data.events || [];
    if (!events.length) {
      el.innerHTML = '<p class="admin-muted">No override changes recorded yet.</p>';
      return;
    }
    el.innerHTML = events.map((e) => {
      const d = e.detail || {};
      const parts = [];
      if (d.added?.length) parts.push(`+${d.added.length} new`);
      if (d.changed?.length) parts.push(`~${d.changed.length} changed`);
      if (d.removed?.length) parts.push(`−${d.removed.length} removed`);
      const detail = parts.join(' · ') || 'updated';
      const keys = [...(d.added || []), ...(d.changed || []), ...(d.removed || [])].slice(0, 3);
      const keyHint = keys.length
        ? `<div style="font-size:11px;color:var(--fg-dim);margin-top:2px">${esc(keys.join(', '))}${(d.added?.length || 0) + (d.changed?.length || 0) + (d.removed?.length || 0) > 3 ? '…' : ''}</div>`
        : '';
      return `<div style="padding:8px 0;border-bottom:1px solid var(--glass-border-soft);font-size:12px">
        <strong>${esc(e.actor_name || '—')}</strong> · ${esc(detail)}${d.total != null ? ` · ${d.total} total` : ''}
        ${keyHint}
        <div style="color:var(--fg-dim);font-size:11px;margin-top:2px">${esc(fmtDateTime(e.created_at))}</div>
      </div>`;
    }).join('');
  } catch (e) {
    el.innerHTML = `<p class="admin-muted">${esc(e.message || 'Could not load history')}</p>`;
  }
}

function closeTfImportModal() {
  const modal = $('tfImportModal');
  if (modal) modal.style.display = 'none';
  tfPendingImport = null;
}

function openTfImportModal(parsed, diff, customDiff) {
  tfPendingImport = parsed;
  const parts = [];
  if (parsed.hasOverrides !== false && Object.keys(parsed.overrides || {}).length) {
    parts.push(`${Object.keys(parsed.overrides).length} override(s)`);
  }
  if (parsed.hasCustomSteps && Object.keys(parsed.custom_steps || {}).length) {
    const count = Object.values(parsed.custom_steps).reduce((n, list) => n + list.length, 0);
    parts.push(`${count} custom step(s)`);
  }
  $('tfImportModalHint').textContent = `${parts.join(' + ') || 'Config'} in file — choose merge or replace.`;
  const html = [
    formatFlowConfigImportSummary(diff, customDiff, esc),
    diff ? formatOverrideDiffHtml(diff, esc) : '',
    customDiff ? `<div style="margin-top:12px">${formatCustomStepsDiffHtml(customDiff, esc)}</div>` : '',
  ].filter(Boolean).join('');
  $('tfImportDiff').innerHTML = html || '<p class="admin-muted">No changes detected.</p>';
  const modal = $('tfImportModal');
  if (modal) modal.style.display = 'flex';
}

async function applyTfImport(mode) {
  if (!tfPendingImport) return;
  try {
    if (tfPendingImport.hasOverrides !== false && Object.keys(tfPendingImport.overrides || {}).length) {
      const nextOverrides = mergeOverrideMaps(getTerminalFlowOverridesMap(), tfPendingImport.overrides, mode);
      await saveTerminalFlowOverrides(nextOverrides);
    }
    if (tfPendingImport.hasCustomSteps && Object.keys(tfPendingImport.custom_steps || {}).length) {
      const nextCustom = mergeCustomStepsMaps(getTerminalFlowCustomStepsMap(), tfPendingImport.custom_steps, mode);
      await saveTerminalFlowCustomSteps(nextCustom);
    }
    showToast('Import applied', 'success');
    closeTfImportModal();
    renderTfDriftBanner();
    renderTfStaleOverrides();
    renderTfStaleCustomSteps();
    renderTfStepsList();
    renderTfStepDetail();
    renderTfCustomStepsPanel();
    await loadTfOverrideHistory();
    if (tfMode === 'simulator') restartTfSimulator();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

async function cleanStaleTfOverrides() {
  const { next, removed } = removeStaleOverrides(getTerminalFlowOverridesMap());
  if (!removed.length) {
    showToast('No stale overrides', 'success');
    return;
  }
  if (!confirm(`Remove ${removed.length} stale override key(s)?`)) return;
  try {
    await saveTerminalFlowOverrides(next);
    showToast(`Removed ${removed.length} stale override(s)`, 'success');
    renderTfDriftBanner();
    renderTfStaleOverrides();
    renderTfStepsList();
    await loadTfOverrideHistory();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

function readTfStepPatchFromForm(step) {
  const patch = {};
  const ask = $('tfAsk')?.value?.trim();
  const hint = $('tfHint')?.value?.trim();
  const choices = $('tfChoices')?.value?.trim();
  if (ask) patch.ask = ask;
  if (hint) patch.hint = hint;
  if (choices) patch.choices = choices;
  if (step?.behaviorEditable && !step?.locked && $('tfOptional')) {
    const val = !!$('tfOptional').checked;
    if (val !== !!step.optional) patch.optional = val;
  }
  return patch;
}

async function duplicateTfOverride(fromFlowId, fromStepKey, toFlowId, toStepKey) {
  if (!toFlowId || !toStepKey) {
    showToast('Pick target flow and step', 'error');
    return;
  }
  const fromFlow = findTerminalFlow(fromFlowId);
  const step = fromFlow ? listCatalogSteps(fromFlow).find((s) => s.key === fromStepKey) : null;
  const targetKey = overrideStorageKey(toFlowId, toStepKey);
  let patch = readTfStepPatchFromForm(step);
  if (!Object.keys(patch).length) {
    patch = { ...(getTerminalFlowOverridesMap()[overrideStorageKey(fromFlowId, fromStepKey)] || {}) };
  }
  if (!Object.keys(patch).length) {
    showToast('Nothing to copy — enter override text first', 'error');
    return;
  }
  const next = { ...getTerminalFlowOverridesMap(), [targetKey]: patch };
  try {
    await saveTerminalFlowOverrides(next);
    showToast(`Copied to ${targetKey}`, 'success');
    tfSelectedFlowId = toFlowId;
    tfSelectedStepKey = toStepKey;
    if ($('tfFlowSelect')) $('tfFlowSelect').value = toFlowId;
    renderTfDriftBanner();
    renderTfStaleOverrides();
    renderTfStepsList();
    renderTfStepDetail();
    await loadTfOverrideHistory();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

function renderTfDriftBanner() {
  const el = $('tfDriftBanner');
  if (!el) return;
  const result = checkTerminalFlowDrift({ overrides: getTerminalFlowOverridesMap(), settings });
  if (result.ok) {
    el.classList.remove('hidden');
    el.classList.add('ok');
    el.innerHTML = `Catalog drift check OK — ${result.summary.flows} flows, ${result.summary.overrides} override(s).`;
    return;
  }
  const preview = result.issues.slice(0, 4).map((i) => esc(i.message)).join('<br>');
  const more = result.issues.length > 4 ? `<br>…and ${result.issues.length - 4} more` : '';
  el.classList.remove('hidden', 'ok');
  el.innerHTML = `<strong>Catalog drift (${result.issues.length})</strong><br>${preview}${more}`;
}

function exportTfOverrides() {
  const overrides = getTerminalFlowOverridesMap();
  downloadOverridesJson(overrides);
  showToast(`Exported ${Object.keys(overrides).length} override(s)`, 'success');
}

async function importTfOverridesFile(file) {
  if (!file) return;
  try {
    const text = await file.text();
    const parsed = parseFlowConfigImport(text);
    if (parsed.error) {
      showToast(parsed.error, 'error');
      return;
    }
    const diff = parsed.hasOverrides !== false
      ? diffOverrideMaps(getTerminalFlowOverridesMap(), parsed.overrides || {})
      : null;
    const customDiff = parsed.hasCustomSteps
      ? diffCustomStepsMaps(getTerminalFlowCustomStepsMap(), parsed.custom_steps || {})
      : null;
    openTfImportModal(parsed, diff, customDiff);
  } catch (e) {
    showToast(e.message || 'Import failed', 'error');
  }
}

async function resetTfFlowOverrides() {
  const flow = findTerminalFlow(tfSelectedFlowId);
  const count = countOverridesForFlow(getTerminalFlowOverridesMap(), tfSelectedFlowId);
  if (!count) {
    showToast('No overrides for this flow', 'dim');
    return;
  }
  if (!confirm(`Remove ${count} override(s) for "${flow?.label || tfSelectedFlowId}"?`)) return;
  const next = filterOverridesForFlow(getTerminalFlowOverridesMap(), tfSelectedFlowId);
  try {
    await saveTerminalFlowOverrides(next);
    showToast('Flow overrides cleared', 'success');
    tfSelectedStepKey = '';
    renderTfDriftBanner();
    renderTfStepsList();
    renderTfStepDetail();
    if (tfMode === 'simulator') restartTfSimulator();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

async function resetAllTfOverrides() {
  const count = Object.keys(getTerminalFlowOverridesMap()).length;
  if (!count) {
    showToast('No overrides saved', 'dim');
    return;
  }
  if (!confirm(`Remove all ${count} terminal flow override(s)?`)) return;
  try {
    await saveTerminalFlowOverrides({});
    showToast('All overrides cleared', 'success');
    tfSelectedStepKey = '';
    renderTfDriftBanner();
    renderTfStepsList();
    renderTfStepDetail();
    if (tfMode === 'simulator') restartTfSimulator();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

function loadTerminalFlowsTab() {
  if (!TAB_PERMS['terminal-flows'](me)) {
    if ($('tfStepsList')) {
      $('tfStepsList').innerHTML = '<p class="admin-muted">You do not have permission to edit terminal flows.</p>';
    }
    return;
  }
  setTerminalFlowOverrides(getTerminalFlowOverridesMap());
  setTerminalFlowCustomSteps(getTerminalFlowCustomStepsMap());
  setTerminalFlowValidationRules(getTerminalFlowValidationRulesMap());
  const select = $('tfFlowSelect');
  if (select && !select.dataset.tfBound) {
    select.dataset.tfBound = '1';
    select.innerHTML = TERMINAL_FLOWS.map((f) => `<option value="${esc(f.id)}">${esc(f.label)}</option>`).join('');
    select.onchange = () => {
      tfSelectedFlowId = select.value;
      tfSelectedStepKey = '';
      renderTfStepsList();
      renderTfStepDetail();
      renderTfCustomStepsPanel();
      renderTfValidationPanel();
      if (tfMode === 'simulator') restartTfSimulator();
    };
    $('tfModeInspector')?.addEventListener('click', () => setTfMode('inspector'));
    $('tfModeSimulator')?.addEventListener('click', () => setTfMode('simulator'));
    $('tfModeAdvanced')?.addEventListener('click', () => setTfMode('advanced'));
    $('tfModeValidation')?.addEventListener('click', () => setTfMode('validation'));
    $('tfCustomSaveBtn')?.addEventListener('click', () => saveTfCustomStep());
    $('tfCustomCancelBtn')?.addEventListener('click', () => resetTfCustomForm());
    $('tfSimForm')?.addEventListener('submit', (e) => {
      e.preventDefault();
      submitTfSimInput();
    });
    $('tfSimRestart')?.addEventListener('click', () => restartTfSimulator());
    $('tfSimMockPreset')?.addEventListener('change', (e) => {
      tfSimMockPreset = e.target.value || 'default';
      restartTfSimulator();
    });
    $('tfSimFillHappy')?.addEventListener('click', () => fillTfSimAnswer('happy'));
    $('tfSimFillInvalid')?.addEventListener('click', () => fillTfSimAnswer('invalid'));
    $('tfSimFillSkip')?.addEventListener('click', () => fillTfSimAnswer('skip'));
    $('tfSimFillDefault')?.addEventListener('click', () => fillTfSimAnswer('default'));
    $('tfSimRunHappy')?.addEventListener('click', () => runTfSimHappyPath());
    $('tfSimContinueHappy')?.addEventListener('click', () => continueTfSimHappyPath());
    $('tfExportBtn')?.addEventListener('click', () => exportTfOverrides());
    $('tfExportConfigBtn')?.addEventListener('click', () => exportTfFlowConfig());
    $('tfExportCustomBtn')?.addEventListener('click', () => exportTfCustomSteps());
    $('tfImportBtn')?.addEventListener('click', () => $('tfImportFile')?.click());
    $('tfImportFile')?.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      importTfOverridesFile(file);
      e.target.value = '';
    });
    $('tfResetFlowBtn')?.addEventListener('click', () => resetTfFlowOverrides());
    $('tfResetAllBtn')?.addEventListener('click', () => resetAllTfOverrides());
    $('tfCleanStaleBtn')?.addEventListener('click', () => cleanStaleTfOverrides());
    $('tfCleanAllStaleBtn')?.addEventListener('click', () => cleanAllTfStale());
    $('tfCleanStaleCustomBtn')?.addEventListener('click', () => cleanStaleTfCustomSteps());
    $('tfDriftCheckBtn')?.addEventListener('click', () => { renderTfDriftBanner(); renderTfStaleOverrides(); renderTfStaleCustomSteps(); });
    $('tfImportModalClose')?.addEventListener('click', closeTfImportModal);
    $('tfImportCancelBtn')?.addEventListener('click', closeTfImportModal);
    $('tfImportMergeBtn')?.addEventListener('click', () => applyTfImport('merge'));
    $('tfImportReplaceBtn')?.addEventListener('click', () => applyTfImport('replace'));
    $('tfStepSearch')?.addEventListener('input', (e) => {
      tfStepSearch = e.target.value || '';
      renderTfStepsList();
      if (tfMode === 'validation') renderTfValidationPanel();
    });
  }
  if (select && select.value) tfSelectedFlowId = select.value;
  else if (!tfSelectedFlowId && TERMINAL_FLOWS[0]) tfSelectedFlowId = TERMINAL_FLOWS[0].id;
  if (select) select.value = tfSelectedFlowId;
  setTfMode(tfMode, { skipRestart: true });
  renderTfDriftBanner();
  renderTfStaleOverrides();
  renderTfStaleCustomSteps();
  loadTfOverrideHistory();
  renderTfStepsList();
  renderTfStepDetail();
  if (tfMode === 'simulator') restartTfSimulator();
}

function setTfMode(mode, opts = {}) {
  tfMode = mode === 'simulator' ? 'simulator'
    : mode === 'advanced' ? 'advanced'
      : mode === 'validation' ? 'validation'
        : 'inspector';
  $('tfModeInspector')?.classList.toggle('btn-primary', tfMode === 'inspector');
  $('tfModeInspector')?.classList.toggle('btn-ghost', tfMode !== 'inspector');
  $('tfModeSimulator')?.classList.toggle('btn-primary', tfMode === 'simulator');
  $('tfModeSimulator')?.classList.toggle('btn-ghost', tfMode !== 'simulator');
  $('tfModeAdvanced')?.classList.toggle('btn-primary', tfMode === 'advanced');
  $('tfModeAdvanced')?.classList.toggle('btn-ghost', tfMode !== 'advanced');
  $('tfModeValidation')?.classList.toggle('btn-primary', tfMode === 'validation');
  $('tfModeValidation')?.classList.toggle('btn-ghost', tfMode !== 'validation');
  $('tfInspectorPanel')?.classList.toggle('hidden', tfMode !== 'inspector');
  $('tfSimulatorPanel')?.classList.toggle('hidden', tfMode !== 'simulator');
  $('tfAdvancedPanel')?.classList.toggle('hidden', tfMode !== 'advanced');
  $('tfValidationPanel')?.classList.toggle('hidden', tfMode !== 'validation');
  if (tfMode === 'simulator' && !opts.skipRestart) restartTfSimulator();
  if (tfMode === 'advanced') {
    resetTfCustomForm();
    renderTfCustomStepsPanel();
  }
  if (tfMode === 'validation') renderTfValidationPanel();
}

function appendTfSimLine(text, className = 'system') {
  const el = $('tfSimTranscript');
  if (!el) return;
  const line = document.createElement('div');
  line.className = `tf-sim-line ${className}`;
  line.textContent = text;
  el.appendChild(line);
  line.scrollIntoView({ block: 'end' });
}

function renderTfSimCurrent(q) {
  const el = $('tfSimCurrent');
  if (!el) return;
  if (!q) {
    el.innerHTML = '<p class="admin-muted">Flow complete or no steps.</p>';
    return;
  }
  el.innerHTML = `
    <p style="font-size:11px;color:var(--fg-dim);margin:0 0 6px">Step ${q.stepIndex + 1} / ${q.totalSteps} · <code>${esc(q.stepKey)}</code>${q.optional ? ' · optional' : ''}</p>
    <dl class="tf-meta">
      <dt>Writes to</dt><dd>${esc(q.meta?.writesTo || '—')}</dd>
      <dt>Shows when</dt><dd>${esc(q.meta?.showsWhen || 'Always')}</dd>
      <dt>Impact</dt><dd>${esc(q.meta?.impact || '—')}</dd>
      <dt>Validation</dt><dd>${esc(q.validationLabel || '—')}</dd>
    </dl>`;
}

function renderTfSimImpact(impact) {
  const el = $('tfSimImpact');
  if (!el) return;
  if (!impact) {
    el.innerHTML = '<p class="admin-muted">Answer a step to see parsed values and state changes.</p>';
    return;
  }
  const changes = (impact.changes || []).map((c) =>
    `<li><code>${esc(c.path)}</code>: ${esc(JSON.stringify(c.before))} → ${esc(JSON.stringify(c.after))}</li>`,
  ).join('');
  el.innerHTML = `
    <p style="font-size:12px;margin:0 0 6px">Parsed: <code>${esc(JSON.stringify(impact.parsed))}</code></p>
    ${impact.meta?.writesTo ? `<p style="font-size:11px;color:var(--fg-dim);margin:0 0 6px">Catalog: ${esc(impact.meta.writesTo)}</p>` : ''}
    ${changes ? `<ul style="margin:0;padding-left:18px;font-size:11px">${changes}</ul>` : '<p class="admin-muted" style="margin:0">No state diff (stage answer stored for patch).</p>'}`;
}

function renderTfSimPreview(completion) {
  const el = $('tfSimPreview');
  if (!el) return;
  el.textContent = completion ? formatSimulatorPreview(completion) : '';
}

function renderTfSimMockPresetSelect() {
  const wrap = $('tfSimStageOpts');
  const select = $('tfSimMockPreset');
  if (!wrap || !select) return;
  const presets = listSimulatorMockPresets(tfSelectedFlowId);
  if (!presets.length) {
    wrap.classList.add('hidden');
    tfSimMockPreset = 'default';
    return;
  }
  wrap.classList.remove('hidden');
  select.innerHTML = presets.map((p) =>
    `<option value="${esc(p.id)}">${esc(p.label)}</option>`,
  ).join('');
  if (!presets.some((p) => p.id === tfSimMockPreset)) tfSimMockPreset = presets[0].id;
  select.value = tfSimMockPreset;
}

function renderTfSimWording() {
  const el = $('tfSimWording');
  if (!el || !tfSimSession) return;
  el.textContent = formatWordingCompareText(getSimulatorWordingCompare(tfSimSession));
}

function setTfSimHappyPaused(paused) {
  tfSimHappyPaused = paused;
  $('tfSimContinueHappy')?.classList.toggle('hidden', !paused);
  $('tfSimRunHappy')?.classList.toggle('hidden', paused);
}

function renderTfSimCompare() {
  const el = $('tfSimCompare');
  if (!el || !tfSimSession) return;
  el.textContent = formatComparePreview(getSimulatorComparePreview(tfSimSession));
}

function renderTfSimQuickBar() {
  const q = tfSimSession && !tfSimSession.done ? getSimulatorQuestion(tfSimSession) : null;
  const skipBtn = $('tfSimFillSkip');
  const defBtn = $('tfSimFillDefault');
  if (skipBtn) skipBtn.disabled = !q?.optional && q?.stepKey !== '_intro';
  if (defBtn) defBtn.disabled = !(q?.defaultValue != null && q.defaultValue !== '');
}

function afterTfSimStep(result) {
  if (result?.impact) renderTfSimImpact(result.impact);
  renderTfSimCompare();
  renderTfSimWording();
  renderTfSimQuickBar();
  if (result?.done || tfSimSession?.done) {
    appendTfSimLine('Simulation complete.', 'success');
    if (tfSimSession?.completion) {
      appendTfSimLine(formatSimulatorPreview(tfSimSession.completion), 'dim');
      renderTfSimPreview(tfSimSession.completion);
    }
    renderTfSimCurrent(null);
    const simInput = $('tfSimInput');
    if (simInput) simInput.disabled = true;
    renderTfSimQuickBar();
    return true;
  }
  const q = getSimulatorQuestion(tfSimSession);
  if (q) {
    appendTfSimLine(q.question, 'system');
    if (q.hint) appendTfSimLine(q.hint, 'dim');
    if (q.defaultValue != null && q.defaultValue !== '') {
      appendTfSimLine(`Default: ${q.defaultValue} — press Enter to keep.`, 'dim');
    }
  }
  renderTfSimCurrent(q);
  const simInput = $('tfSimInput');
  if (simInput) {
    simInput.disabled = false;
    simInput.focus();
  }
  return false;
}

function processTfSimAnswer(text) {
  if (!tfSimSession) restartTfSimulator();
  appendTfSimLine(text || '(enter)', 'agent');
  const result = submitSimulatorAnswer(tfSimSession, text);
  if (result.error) {
    appendTfSimLine(result.error, 'error');
    renderTfSimCurrent(getSimulatorQuestion(tfSimSession));
    renderTfSimCompare();
    renderTfSimWording();
    renderTfSimQuickBar();
    return;
  }
  afterTfSimStep(result);
}

function fillTfSimAnswer(mode) {
  if (!tfSimSession || tfSimSession.done) return;
  const answer = suggestSimulatorAnswer(tfSimSession, mode);
  if (answer == null) {
    showToast(mode === 'skip' ? 'This step is required' : 'No suggestion for this step', 'error');
    return;
  }
  const input = $('tfSimInput');
  if (input) input.value = answer;
  processTfSimAnswer(answer);
}

function runTfSimHappyPath() {
  if (!tfSimSession || tfSimSession.done) restartTfSimulator();
  setTfSimHappyPaused(false);
  const pauseOnConfirm = !!$('tfSimPauseConfirm')?.checked;
  if (pauseOnConfirm) appendTfSimLine('Running happy path (pauses on confirm steps)…', 'dim');
  else appendTfSimLine('Running happy path…', 'dim');
  advanceTfSimHappyPath(pauseOnConfirm);
}

function continueTfSimHappyPath() {
  if (!tfSimSession || tfSimSession.done || !tfSimHappyPaused) return;
  const input = $('tfSimInput');
  const answer = input?.value?.trim();
  if (!answer) {
    showToast('No answer to submit — fill or use Happy', 'error');
    return;
  }
  setTfSimHappyPaused(false);
  processTfSimAnswer(answer);
  if (tfSimSession?.done) return;
  const pauseOnConfirm = !!$('tfSimPauseConfirm')?.checked;
  advanceTfSimHappyPath(pauseOnConfirm);
}

function advanceTfSimHappyPath(pauseOnConfirm) {
  while (!tfSimSession?.done) {
    const run = runSimulatorHappyPath(tfSimSession, { pauseOnConfirm });
    run.steps.forEach((s) => {
      appendTfSimLine(s.answer, 'agent');
      if (s.error) appendTfSimLine(s.error, 'error');
    });
    if (run.paused) {
      appendTfSimLine(`Paused at confirm step: ${run.pauseStep?.stepKey}`, 'dim');
      if (run.pauseStep?.question) appendTfSimLine(run.pauseStep.question, 'system');
      const input = $('tfSimInput');
      if (input) {
        input.value = run.pauseAnswer || '';
        input.disabled = false;
        input.focus();
      }
      renderTfSimCurrent(run.pauseStep);
      renderTfSimCompare();
      renderTfSimWording();
      renderTfSimQuickBar();
      setTfSimHappyPaused(true);
      return;
    }
    if (run.error) {
      appendTfSimLine(`Stopped: ${run.error}`, 'error');
      renderTfSimCurrent(getSimulatorQuestion(tfSimSession));
      renderTfSimCompare();
      renderTfSimWording();
      renderTfSimPreview(null);
      renderTfSimQuickBar();
      return;
    }
    if (run.completion || tfSimSession?.done) {
      afterTfSimStep({ done: true, completion: tfSimSession.completion });
      return;
    }
  }
}

function restartTfSimulator() {
  const transcript = $('tfSimTranscript');
  if (transcript) transcript.innerHTML = '';
  setTfSimHappyPaused(false);
  renderTfSimMockPresetSelect();
  tfSimSession = createSimulatorSession(tfSelectedFlowId, {
    settings,
    overrides: getTerminalFlowOverridesMap(),
    mockPresetId: tfSimMockPreset,
  });
  if (!tfSimSession) {
    appendTfSimLine('Could not start simulation for this flow.', 'error');
    return;
  }
  const flow = findTerminalFlow(tfSelectedFlowId);
  appendTfSimLine(`Simulating: ${flow?.label || tfSelectedFlowId}`, 'system');
  const presetLabel = listSimulatorMockPresets(tfSelectedFlowId)
    .find((p) => p.id === tfSimMockPreset)?.label;
  if (presetLabel) appendTfSimLine(`Scenario: ${presetLabel}`, 'dim');
  if (tfSimSession.done && tfSimSession.completion?.empty) {
    appendTfSimLine(tfSimSession.completion.message, 'dim');
    renderTfSimCurrent(null);
    renderTfSimPreview(null);
    renderTfSimCompare();
    renderTfSimWording();
    return;
  }
  const q = getSimulatorQuestion(tfSimSession);
  if (q) {
    appendTfSimLine(q.question, 'system');
    if (q.hint) appendTfSimLine(q.hint, 'dim');
    if (q.defaultValue != null && q.defaultValue !== '') {
      appendTfSimLine(`Default: ${q.defaultValue} — press Enter to keep.`, 'dim');
    }
  }
  renderTfSimCurrent(q);
  renderTfSimImpact(null);
  renderTfSimPreview(null);
  renderTfSimCompare();
  renderTfSimWording();
  renderTfSimQuickBar();
  const input = $('tfSimInput');
  if (input) {
    input.disabled = false;
    input.focus();
  }
}

function submitTfSimInput() {
  const input = $('tfSimInput');
  const text = input?.value ?? '';
  if (input) input.value = '';
  processTfSimAnswer(text);
}

function renderTfStepsList() {
  const el = $('tfStepsList');
  if (!el) return;
  const flow = findTerminalFlow(tfSelectedFlowId);
  if (!flow) {
    el.innerHTML = '<p class="admin-muted">No flow selected.</p>';
    return;
  }
  const overrides = getTerminalFlowOverridesMap();
  const valRules = getTerminalFlowValidationRulesMap();
  const q = tfStepSearch.trim().toLowerCase();
  const steps = listCatalogSteps(flow).filter((s) => {
    if (!q) return true;
    const hay = [s.key, s.label, s.ask, s.hint, s.choices].join(' ').toLowerCase();
    return hay.includes(q);
  });
  if (!steps.length) {
    el.innerHTML = '<p class="admin-muted">No steps match your search.</p>';
    return;
  }
  el.innerHTML = steps.map((s) => {
    const hasOverride = !!overrides[overrideStorageKey(flow.id, s.key)];
    const hasValRule = !!valRules[validationStorageKey(flow.id, s.key)];
    const active = s.key === tfSelectedStepKey ? ' tf-step-active' : '';
    const badge = s.pattern ? ' <span style="font-size:10px;color:var(--fg-dim)">template</span>' : '';
    const customBadge = s.custom ? ' <span style="font-size:10px;color:var(--orange)">custom</span>' : '';
    const dot = hasOverride ? ' · <span style="color:var(--accent)">copy</span>' : '';
    const valDot = hasValRule ? ' · <span style="color:var(--green,#4ade80)">rules</span>' : '';
    return `<button type="button" class="tf-step-btn${active}" data-tf-step="${esc(s.key)}">${esc(s.label || s.key)}${badge}${customBadge}${dot}${valDot}</button>`;
  }).join('');

  el.querySelectorAll('[data-tf-step]').forEach((btn) => {
    btn.onclick = () => {
      tfSelectedStepKey = btn.dataset.tfStep;
      renderTfStepsList();
      renderTfStepDetail();
    };
  });
}

function renderTfStepDetail() {
  const el = $('tfStepDetail');
  if (!el) return;
  const flow = findTerminalFlow(tfSelectedFlowId);
  if (!flow || !tfSelectedStepKey) {
    el.innerHTML = '<p class="admin-muted">Select a step to inspect impact and edit copy overrides.</p>';
    return;
  }
  const step = listCatalogSteps(flow).find((s) => s.key === tfSelectedStepKey);
  if (!step) {
    el.innerHTML = '<p class="admin-muted">Step not found in catalog.</p>';
    return;
  }
  const override = getTfOverride(flow.id, step.key) || {};
  const valOverride = getTfValidationOverride(flow.id, step.key);
  const rule = resolveValidationRule(flow.id, step.key, step);
  const validationHtml = buildTfValidationEditorHtml(flow, step, rule, valOverride);
  const storageKey = overrideStorageKey(flow.id, step.key);
  const effectiveOptional = override.optional != null ? !!override.optional : !!step.optional;
  const behaviorBlock = step.behaviorEditable && !step.locked ? `
    <div class="form-group">
      <label class="tf-sim-opt" style="display:flex;align-items:center;gap:6px;font-size:12px">
        <input type="checkbox" id="tfOptional"${effectiveOptional ? ' checked' : ''}>
        Optional step (agents can skip with -)
      </label>
      <p class="admin-muted" style="margin-top:4px">Catalog default: ${step.optional ? 'optional' : 'required'}. Only safe steps allow this toggle.</p>
    </div>` : step.locked ? '<p class="admin-muted" style="font-size:11px;color:var(--orange)">This step is locked — behavior cannot be changed from admin.</p>' : '';
  const customNote = step.custom ? '<p class="admin-muted" style="font-size:11px">Custom step — edit or remove in the <strong>Advanced</strong> tab.</p>' : '';
  el.innerHTML = `
    <h3 style="margin:0 0 4px;font-size:15px">${esc(step.label || step.key)}</h3>
    <p style="font-size:11px;color:var(--fg-dim);margin:0 0 12px"><code>${esc(step.key)}</code>${step.pattern ? ' · applies to all matching runtime keys' : ''}${step.custom ? ' · custom step' : ''}</p>
    ${customNote}
    <dl class="tf-meta">
      <dt>Writes to</dt><dd>${esc(step.writesTo || '—')}</dd>
      <dt>Shows when</dt><dd>${esc(step.showsWhen || 'Always')}</dd>
      <dt>Impact</dt><dd>${esc(step.impact || '—')}</dd>
      <dt>Optional</dt><dd>${effectiveOptional ? 'Yes' : 'No'}</dd>
    </dl>
    ${step.custom ? '' : `
    <div class="form-group">
      <label class="form-label" for="tfAsk">Question (ask)</label>
      <textarea class="form-textarea" id="tfAsk" rows="3" placeholder="${esc(step.ask)}">${esc(override.ask ?? '')}</textarea>
      <p class="admin-muted" style="margin-top:4px">Default: ${esc(step.ask)}</p>
    </div>
    <div class="form-group">
      <label class="form-label" for="tfHint">Hint</label>
      <input class="form-input" id="tfHint" placeholder="${esc(step.hint || 'none')}" value="${esc(override.hint ?? '')}">
      <p class="admin-muted" style="margin-top:4px">Default: ${esc(step.hint || '—')}</p>
    </div>
    ${step.choices ? `
    <div class="form-group">
      <label class="form-label" for="tfChoices">Choice labels</label>
      <input class="form-input" id="tfChoices" placeholder="${esc(step.choices)}" value="${esc(override.choices ?? '')}">
      <p class="admin-muted" style="margin-top:4px">Default: ${esc(step.choices)} · appended to hint in terminal</p>
    </div>` : ''}
    ${behaviorBlock}
    ${validationHtml}
    <div style="display:flex;gap:8px;flex-wrap:wrap">
      <button class="btn btn-primary btn-sm" type="button" id="tfSaveBtn">Save override</button>
      <button class="btn btn-ghost btn-sm" type="button" id="tfResetBtn">Reset to default</button>
    </div>
    <div class="form-group" style="margin-top:14px;padding-top:14px;border-top:1px solid var(--glass-border-soft)">
      <label class="form-label">Duplicate override to</label>
      <div class="form-row">
        <select class="form-select" id="tfDupFlow">${TERMINAL_FLOWS.map((f) => `<option value="${esc(f.id)}"${f.id === flow.id ? ' selected' : ''}>${esc(f.label)}</option>`).join('')}</select>
        <select class="form-select" id="tfDupStep"></select>
      </div>
      <button class="btn btn-ghost btn-sm" type="button" id="tfDupBtn" style="margin-top:8px">Copy to step</button>
    </div>`}
    <p class="admin-muted" style="margin-top:10px;font-size:11px">Storage key: <code>${esc(storageKey)}</code></p>`;

  if (step.custom) return;

  const dupFlow = $('tfDupFlow');
  const dupStep = $('tfDupStep');
  const fillDupSteps = () => {
    const fid = dupFlow?.value || flow.id;
    const f = findTerminalFlow(fid);
    if (!dupStep || !f) return;
    dupStep.innerHTML = listCatalogSteps(f).map((s) =>
      `<option value="${esc(s.key)}">${esc(s.label || s.key)}</option>`,
    ).join('');
  };
  fillDupSteps();
  dupFlow.onchange = fillDupSteps;
  $('tfSaveBtn').onclick = () => saveTfStepOverride(flow.id, step.key);
  $('tfResetBtn').onclick = () => resetTfStepOverride(flow.id, step.key);
  $('tfDupBtn').onclick = () => duplicateTfOverride(flow.id, step.key, dupFlow.value, dupStep.value);
  $('tfValSaveBtn')?.addEventListener('click', () => saveTfValidationOverride(flow.id, step.key, step));
  $('tfValResetBtn')?.addEventListener('click', () => resetTfValidationOverride(flow.id, step.key));
}

async function saveTfValidationOverride(flowId, stepKey, catalogStep) {
  if (!TAB_PERMS['terminal-flows'](me)) return;
  const rule = resolveValidationRule(flowId, stepKey, catalogStep);
  const patch = readTfValidationPatchFromForm(rule);
  const key = validationStorageKey(flowId, stepKey);
  const next = { ...getTerminalFlowValidationRulesMap() };
  if (!patch) delete next[key];
  else next[key] = patch;
  try {
    await saveTerminalFlowValidationRules(next);
    showToast('Validation rules saved', 'success');
    renderTfStepsList();
    renderTfStepDetail();
    renderTfValidationPanel();
    if (tfMode === 'simulator') restartTfSimulator();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

async function resetTfValidationOverride(flowId, stepKey) {
  if (!TAB_PERMS['terminal-flows'](me)) return;
  const key = validationStorageKey(flowId, stepKey);
  const next = { ...getTerminalFlowValidationRulesMap() };
  delete next[key];
  try {
    await saveTerminalFlowValidationRules(next);
    showToast('Validation reset to default', 'success');
    renderTfStepsList();
    renderTfStepDetail();
    renderTfValidationPanel();
    if (tfMode === 'simulator') restartTfSimulator();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

async function saveTfStepOverride(flowId, stepKey) {
  if (!TAB_PERMS['terminal-flows'](me)) return;
  const flow = findTerminalFlow(flowId);
  const step = flow ? listCatalogSteps(flow).find((s) => s.key === stepKey) : null;
  const key = overrideStorageKey(flowId, stepKey);
  const next = { ...getTerminalFlowOverridesMap() };
  const patch = readTfStepPatchFromForm(step);
  if (!Object.keys(patch).length) {
    delete next[key];
  } else {
    next[key] = patch;
  }
  try {
    await saveTerminalFlowOverrides(next);
    showToast('Flow copy saved', 'success');
    renderTfDriftBanner();
    renderTfStaleOverrides();
    renderTfStepsList();
    await loadTfOverrideHistory();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

async function resetTfStepOverride(flowId, stepKey) {
  if (!TAB_PERMS['terminal-flows'](me)) return;
  const key = overrideStorageKey(flowId, stepKey);
  const next = { ...getTerminalFlowOverridesMap() };
  delete next[key];
  try {
    await saveTerminalFlowOverrides(next);
    showToast('Override removed', 'success');
    renderTfDriftBanner();
    renderTfStaleOverrides();
    renderTfStepDetail();
    renderTfStepsList();
    await loadTfOverrideHistory();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

function renderAgents(agents) {
  if (!$('agentsList')) return;
  $('agentsList').innerHTML = agents.map((a) => {
    const depts = (a.departments || []).map((d) => DEPT_LABELS[d] || d).join(', ') || '—';
    return `
    <div class="agent-row">
      <div style="display:flex;align-items:center;gap:10px">
        <span class="agent-dot" style="background:${esc(a.color)};width:12px;height:12px"></span>
        <div>
          <div style="font-weight:600">${esc(a.name)} <span style="font-size:11px;color:var(--fg-dim)">@${esc(a.username)}</span></div>
          <div style="font-size:11px;color:var(--fg-muted)">${esc((a.permissions&&a.permissions.view_only)?'viewer (owner)':a.role)}${a.active ? '' : ' · inactive'}${a.must_change_password ? ' · must change password' : ''}</div>
          <div style="font-size:11px;color:var(--fg-dim)">${esc(depts)}${a.primary_department ? ` · primary: ${esc(DEPT_LABELS[a.primary_department] || a.primary_department)}` : ''}</div>
        </div>
      </div>
      <button class="btn btn-ghost btn-sm" data-edit="${esc(a.id)}">Edit</button>
    </div>`;
  }).join('');

  $('agentsList').querySelectorAll('[data-edit]').forEach((btn) => {
    btn.onclick = () => openEdit(agents.find((x) => x.id === btn.dataset.edit));
  });
}

function readDepartmentFields(prefix) {
  const attr = prefix === 'new' ? 'data-new-dept' : 'data-edit-dept';
  const departments = DEPARTMENTS.filter((d) => document.querySelector(`[${attr}="${d}"]`)?.checked);
  const primarySelect = $(prefix === 'new' ? 'newPrimaryDept' : 'editPrimaryDept');
  const primary_department = primarySelect?.value || departments[0] || 'tour';
  return { departments, primary_department };
}

function syncPrimaryDeptOptions(prefix) {
  const { departments } = readDepartmentFields(prefix);
  const sel = $(prefix === 'new' ? 'newPrimaryDept' : 'editPrimaryDept');
  if (!sel) return;
  const cur = sel.value;
  sel.innerHTML = (departments.length ? departments : DEPARTMENTS)
    .map((d) => `<option value="${d}">${esc(DEPT_LABELS[d] || d)}</option>`)
    .join('');
  sel.value = departments.includes(cur) ? cur : (departments[0] || 'tour');
}

function setDepartmentFields(prefix, agent = {}) {
  const attr = prefix === 'new' ? 'data-new-dept' : 'data-edit-dept';
  const departments = agent.departments?.length ? agent.departments : [...DEPARTMENTS];
  DEPARTMENTS.forEach((d) => {
    const cb = document.querySelector(`[${attr}="${d}"]`);
    if (cb) cb.checked = departments.includes(d);
  });
  syncPrimaryDeptOptions(prefix);
  const sel = $(prefix === 'new' ? 'newPrimaryDept' : 'editPrimaryDept');
  if (sel && agent.primary_department) sel.value = agent.primary_department;
}

function openEdit(a) {
  if (!a) return;
  $('editId').value = a.id;
  $('editName').value = a.name;
  $('editRole').value = (a.permissions && a.permissions.view_only) ? 'viewer' : a.role;
  $('editRole').onchange = () => {
    const viewer = $('editRole').value === 'viewer';
    $('permGrid').querySelectorAll('[data-perm]').forEach((cb) => { cb.disabled = viewer; });
  };
  $('editColor').value = a.color || '#a78bfa';
  resetPasswordInput($('editPass'));
  $('editActive').checked = !!a.active;
  if ($('editMustChange')) $('editMustChange').checked = !!a.must_change_password;
  editingPerms = { ...a.permissions };
  $('permGrid').innerHTML = PERM_KEYS.map(([k, label]) => `
    <label class="perm-item">
      <input type="checkbox" data-perm="${k}" ${editingPerms[k] ? 'checked' : ''}>
      ${label}
    </label>
  `).join('');
  $('permGrid').querySelectorAll('[data-perm]').forEach((cb) => {
    cb.onchange = () => { editingPerms[cb.dataset.perm] = cb.checked; };
  });
  const isViewerRole = $('editRole').value === 'viewer';
  $('permGrid').querySelectorAll('[data-perm]').forEach((cb) => {
    cb.disabled = isViewerRole;
  });
  $('editDeptGrid')?.classList.remove('hidden');
  $('editPrimaryDept')?.closest('.form-group')?.classList.remove('hidden');
  setDepartmentFields('edit', a);
  $('editAgentModal').classList.add('open');
}

async function saveAgentEdit() {
  const id = $('editId').value;
  const body = {
    name: $('editName').value.trim(),
    role: $('editRole').value,
    color: $('editColor').value,
    active: $('editActive').checked,
    must_change_password: !!$('editMustChange')?.checked,
    permissions: editingPerms,
  };
  if ($('editPass').value) {
    if ($('editPass').value.length < 8) {
      showToast('Password must be at least 8 characters', 'error');
      return;
    }
    body.password = $('editPass').value;
  }
  if ($('editRole').value !== 'viewer') {
    const depts = readDepartmentFields('edit');
    if (!depts.departments.length) {
      showToast('Select at least one department', 'error');
      return;
    }
    body.departments = depts.departments;
    body.primary_department = depts.primary_department;
  }
  try {
    await api('/api/agents/' + id, { method: 'PUT', body });
    showToast('Agent updated', 'success');
    $('editAgentModal').classList.remove('open');
    await loadAll();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

async function createAgent() {
  const name = $('newName').value.trim();
  const username = $('newUser').value.trim();
  if (!name || !username) {
    showToast('Name and username are required', 'error');
    return;
  }
  const password = $('newPass').value;
  if (password.length < 8) {
    showToast('Password must be at least 8 characters', 'error');
    return;
  }
  const role = $('newRole').value;
  const payload = {
    name: $('newName').value.trim(),
    username: $('newUser').value.trim(),
    password,
    role,
    color: $('newColor').value,
    must_change_password: !!$('newMustChange')?.checked,
  };
  if (role !== 'viewer') {
    const depts = readDepartmentFields('new');
    if (!depts.departments.length) {
      showToast('Select at least one department', 'error');
      return;
    }
    payload.departments = depts.departments;
    payload.primary_department = depts.primary_department;
  }
  try {
    await api('/api/agents', {
      method: 'POST',
      body: payload,
    });
    showToast('Agent created', 'success');
    $('newName').value = '';
    $('newUser').value = '';
    resetPasswordInput($('newPass'));
    await loadAll();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

function renderLists() {
  if (!TAB_PERMS.lists(me)) {
    ['toursList', 'visasList', 'sourcesList', 'airlinesList'].forEach((id) => {
      if ($(id)) $(id).innerHTML = '<p class="list-empty">You do not have permission to edit lists.</p>';
    });
    return;
  }

  const renderTags = (key, elId) => {
    const items = settings[key] || [];
    $(elId).innerHTML = items.map((item, i) => `
      <span class="tag">${esc(item)} <button data-key="${key}" data-i="${i}">&times;</button></span>
    `).join('');
    $(elId).querySelectorAll('button').forEach((btn) => {
      btn.onclick = async () => {
        try {
        const k = btn.dataset.key;
        const arr = [...(settings[k] || [])];
        const removed = arr.splice(Number(btn.dataset.i), 1)[0];
        const body = { [k]: arr };
        if (k === 'visas' && removed) {
          const costs = { ...(settings.visa_costs || {}) };
          delete costs[removed];
          body.visa_costs = costs;
        }
        await api('/api/settings', { method: 'PUT', body });
        settings[k] = arr;
        if (body.visa_costs) settings.visa_costs = body.visa_costs;
        renderLists();
        } catch (e) {
          showToast(e.message, 'error');
        }
      };
    });
  };

  const visas = settings.visas || [];
  const costs = settings.visa_costs || {};
  $('visasList').innerHTML = visas.length ? visas.map((item, i) => `
    <div class="list-row">
      <span class="list-row-name">${esc(item)}</span>
      <input type="number" class="form-input list-cost" data-visa="${esc(item)}" value="${costs[item] ?? ''}" placeholder="Default LKR" min="0">
      <button class="btn btn-ghost btn-sm" data-key="visas" data-i="${i}" title="Remove">&times;</button>
    </div>
  `).join('') : '<p class="list-empty">No visa types yet.</p>';
  $('visasList').querySelectorAll('.list-cost').forEach((inp) => {
    inp.onchange = async () => {
      try {
      const next = { ...(settings.visa_costs || {}) };
      const v = inp.dataset.visa;
      const n = inp.value.trim();
      if (n) next[v] = Number(n);
      else delete next[v];
      await api('/api/settings', { method: 'PUT', body: { visa_costs: next } });
      settings.visa_costs = next;
      showToast('Default cost saved', 'success');
      } catch (e) {
        showToast(e.message, 'error');
      }
    };
  });
  $('visasList').querySelectorAll('button[data-key="visas"]').forEach((btn) => {
    btn.onclick = async () => {
      try {
      const arr = [...(settings.visas || [])];
      const removed = arr.splice(Number(btn.dataset.i), 1)[0];
      const nextCosts = { ...(settings.visa_costs || {}) };
      if (removed) delete nextCosts[removed];
      await api('/api/settings', { method: 'PUT', body: { visas: arr, visa_costs: nextCosts } });
      settings.visas = arr;
      settings.visa_costs = nextCosts;
      renderLists();
      } catch (e) {
        showToast(e.message, 'error');
      }
    };
  });

  renderTags('tours', 'toursList');
  renderTags('sources', 'sourcesList');
  renderTags('airlines', 'airlinesList');
}

function parseGoogleCseCx(raw) {
  let s = String(raw || '').trim();
  if (!s) return '';
  const fromUrl = s.match(/[?&]cx=([a-zA-Z0-9:_-]+)/);
  if (fromUrl) return fromUrl[1];
  const fromAttr = s.match(/\bcx[=:]\s*['"]?([a-zA-Z0-9:_-]+)/i);
  if (fromAttr) return fromAttr[1];
  s = s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (/^[a-zA-Z0-9:_-]+$/.test(s)) return s;
  return s;
}

function renderGeneralSettings() {
  if (!TAB_PERMS.lists(me)) return;
  if ($('settingTimezone')) $('settingTimezone').value = settings.timezone || 'Asia/Colombo';
  if ($('settingDefaultStatus')) $('settingDefaultStatus').value = settings.default_status || 'new';
  if ($('settingGoogleCseCx')) $('settingGoogleCseCx').value = parseGoogleCseCx(settings.google_cse_cx || '');
}

async function saveGeneralSettings() {
  if (!TAB_PERMS.lists(me)) return;
  try {
    const body = {
      timezone: $('settingTimezone')?.value?.trim() || 'Asia/Colombo',
      default_status: $('settingDefaultStatus')?.value || 'new',
      google_cse_cx: parseGoogleCseCx($('settingGoogleCseCx')?.value || ''),
    };
    await api('/api/settings', { method: 'PUT', body });
    settings.timezone = body.timezone;
    settings.default_status = body.default_status;
    settings.google_cse_cx = body.google_cse_cx;
    showToast('General settings saved', 'success');
  } catch (e) {
    showToast(e.message, 'error');
  }
}

function currentPipelinesFromSettings() {
  const custom = settings.status_pipelines;
  if (custom && typeof custom === 'object') {
    return {
      tour: Array.isArray(custom.tour) && custom.tour.length ? custom.tour : DEFAULT_PIPELINES.tour,
      visa: Array.isArray(custom.visa) && custom.visa.length ? custom.visa : DEFAULT_PIPELINES.visa,
      ticket: Array.isArray(custom.ticket) && custom.ticket.length ? custom.ticket : DEFAULT_PIPELINES.ticket,
    };
  }
  return structuredClone(DEFAULT_PIPELINES);
}

function renderPipelines() {
  if (!TAB_PERMS.pipelines(me)) return;
  const pipes = currentPipelinesFromSettings();
  if ($('pipelineTour')) $('pipelineTour').value = pipes.tour.join('\n');
  if ($('pipelineVisa')) $('pipelineVisa').value = pipes.visa.join('\n');
  if ($('pipelineTicket')) $('pipelineTicket').value = pipes.ticket.join('\n');
}

function parsePipelineText(raw) {
  return String(raw || '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

async function savePipelines() {
  if (!TAB_PERMS.pipelines(me)) return;
  const body = {
    status_pipelines: {
      tour: parsePipelineText($('pipelineTour')?.value),
      visa: parsePipelineText($('pipelineVisa')?.value),
      ticket: parsePipelineText($('pipelineTicket')?.value),
    },
  };
  for (const [type, list] of Object.entries(body.status_pipelines)) {
    if (!list.length || !list.includes('new')) {
      showToast(`${type} pipeline must include "new"`, 'error');
      return;
    }
  }
  try {
    await api('/api/settings', { method: 'PUT', body });
    settings.status_pipelines = body.status_pipelines;
    showToast('Pipelines saved', 'success');
  } catch (e) {
    showToast(e.message, 'error');
  }
}

async function resetPipelines() {
  if (!TAB_PERMS.pipelines(me)) return;
  if (!confirm('Reset all pipelines to factory defaults?')) return;
  try {
    await api('/api/settings', { method: 'PUT', body: { status_pipelines: DEFAULT_PIPELINES } });
    settings.status_pipelines = structuredClone(DEFAULT_PIPELINES);
    renderPipelines();
    showToast('Pipelines reset', 'success');
  } catch (e) {
    showToast(e.message, 'error');
  }
}

async function addVisaWithCost() {
  const val = $('newVisa').value.trim();
  if (!val) return;
  try {
    const arr = [...(settings.visas || []), val];
    const costs = { ...(settings.visa_costs || {}) };
    const costVal = $('newVisaCost')?.value?.trim();
    if (costVal) costs[val] = Number(costVal);
    await api('/api/settings', { method: 'PUT', body: { visas: arr, visa_costs: costs } });
    settings.visas = arr;
    settings.visa_costs = costs;
    $('newVisa').value = '';
    if ($('newVisaCost')) $('newVisaCost').value = '';
    renderLists();
    showToast('Visa type saved', 'success');
  } catch (e) {
    showToast(e.message || 'Could not save visa', 'error');
  }
}

async function addListItem(key, inputId) {
  const val = $(inputId).value.trim();
  if (!val) return;
  try {
    const arr = [...(settings[key] || []), val];
    await api('/api/settings', { method: 'PUT', body: { [key]: arr } });
    settings[key] = arr;
    $(inputId).value = '';
    renderLists();
    showToast('Saved', 'success');
  } catch (e) {
    showToast(e.message || 'Could not save', 'error');
  }
}

async function loadRecycle() {
  try {
  const data = await api('/api/leads?deleted=1&limit=100');
  const leads = data.leads || [];
  const canPurge = me.role === 'admin';
  if (!leads.length) {
    $('recycleList').innerHTML = '<p style="color:var(--fg-dim);font-size:13px">Recycle bin is empty.</p>';
    return;
  }
  $('recycleList').innerHTML = leads.map((l) => `
    <div class="agent-row">
      <div>
        <div style="font-weight:600">${esc(l.name)} · ${esc(l.type)}</div>
        <div style="font-size:11px;color:var(--fg-muted)">${esc(l.phone)} · deleted ${fmtDate(l.deleted_at)}</div>
      </div>
      <div style="display:flex;gap:6px">
        <button class="btn btn-success btn-sm" data-restore="${esc(l.id)}">Restore</button>
        ${canPurge ? `<button class="btn btn-danger btn-sm" data-purge="${esc(l.id)}">Purge</button>` : ''}
      </div>
    </div>
  `).join('');
  $('recycleList').querySelectorAll('[data-restore]').forEach((b) => {
    b.onclick = async () => {
      try {
      await api('/api/leads/' + b.dataset.restore + '/restore', { method: 'POST' });
      showToast('Restored', 'success');
      loadRecycle();
      } catch (e) {
        showToast(e.message, 'error');
      }
    };
  });
  $('recycleList').querySelectorAll('[data-purge]').forEach((b) => {
    b.onclick = async () => {
      if (!confirm('Permanently delete this inquiry?')) return;
      try {
      await api('/api/leads/' + b.dataset.purge + '/purge', { method: 'POST' });
      showToast('Purged', 'warning');
      loadRecycle();
      } catch (e) {
        showToast(e.message, 'error');
      }
    };
  });
  } catch (e) {
    $('recycleList').innerHTML = `<p style="color:var(--red);font-size:13px">${esc(e.message)}</p>`;
  }
}

async function loadAudit(append = false) {
  try {
  if (!append) {
    auditPage = 1;
    $('auditList').innerHTML = '';
  }
  const data = await api(`/api/audit?page=${auditPage}`);
  auditTotal = data.total || 0;
  const events = data.events || [];
  const html = events.map((e) => `
    <div style="padding:10px 0;border-bottom:1px solid var(--border);font-size:12px">
      <strong>${esc(e.actor_name || '—')}</strong> · ${esc(e.action)}
      ${e.lead_id ? ` · lead <code>${esc(e.lead_id)}</code>` : ''}
      <div style="color:var(--fg-dim);margin-top:2px">${esc(fmtDateTime(e.created_at))}</div>
    </div>
  `).join('');
  if (append) $('auditList').insertAdjacentHTML('beforeend', html);
  else $('auditList').innerHTML = html || '<p style="color:var(--fg-dim)">No events yet.</p>';
  const loaded = auditPage * (data.limit || 50);
  $('auditLoadMoreBtn')?.classList.toggle('hidden', loaded >= auditTotal || !events.length);
  } catch (e) {
    if (!append) $('auditList').innerHTML = `<p style="color:var(--red);font-size:13px">${esc(e.message)}</p>`;
  }
}

const ACTIVITY_LABELS = {
  'lead.view': 'Viewed inquiry',
  'lead.view.client': 'Viewed inquiry (client)',
  'lead.create': 'Created inquiry',
  'lead.update': 'Updated inquiry',
  'lead.status': 'Changed status',
  'lead.delete': 'Deleted inquiry',
  'lead.restore': 'Restored inquiry',
  'lead.purge': 'Purged inquiry',
  'lead.call': 'Logged call',
  'lead.note': 'Added note',
  'lead.merge': 'Merged inquiries',
  'lead.bulk_followup': 'Bulk follow-up',
  'lead.balance_collect': 'Collected balance',
  'lead.request_edit': 'Requested edit access',
  'lead.takeover': 'Took over inquiry',
  'lead.grant_edit': 'Granted edit access',
  'lead.deny_edit': 'Denied edit access',
  'auth.login': 'Signed in',
  'auth.logout': 'Signed out',
  'auth.password_change': 'Changed password',
  'assistant.command': 'Terminal command',
  'assistant.search': 'Terminal search',
  'assistant.browse': 'Terminal browse',
  'data.export': 'Exported data',
  'leads.import': 'Imported leads',
  'agent.create': 'Created agent',
  'agent.update': 'Updated agent',
  'settings.update': 'Updated settings',
  'terminal_flow_overrides.update': 'Terminal flow overrides',
  'terminal_flow_custom_steps.update': 'Terminal flow custom steps',
  'terminal_flow_validation_rules.update': 'Terminal flow validation rules',
  'agent_targets.update': 'Updated targets',
  'assistant_kb.create': 'Added KB entry',
  'assistant_kb.update': 'Updated KB entry',
  'assistant_kb.delete': 'Deleted KB entry',
  'reminder.create': 'Created reminder',
  'reminder.ack': 'Acknowledged reminder',
  'reminder.postpone': 'Postponed reminder',
  'reminder.delete': 'Deleted reminder',
  'activity.purge': 'Cleared activity log',
};

function activityLabel(action) {
  return ACTIVITY_LABELS[action] || action;
}

function isRecentlyActive(iso) {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() < 15 * 60 * 1000;
}

function renderActivityEvent(e) {
  const detail = e.detail && Object.keys(e.detail).length ? JSON.stringify(e.detail, null, 2) : '';
  const leadLink = e.resource_type === 'lead' && e.resource_id
    ? `<a href="/leads/?open=${encodeURIComponent(e.resource_id)}" target="_blank" rel="noopener">${esc(e.lead_name || e.resource_id)}</a>`
    : '';
  const commandLine = e.action === 'assistant.command' && e.detail?.command
    ? `<div style="margin-top:6px;font-family:ui-monospace,monospace;color:var(--fg-muted)">&gt; ${esc(e.detail.command)}</div>`
    : '';
  return `
    <div class="activity-event">
      <div class="activity-event-head">
        <strong>${esc(e.agent_name || '—')}</strong>
        <span class="activity-action-tag">${esc(activityLabel(e.action))}</span>
        ${leadLink ? `<span>· ${leadLink}</span>` : ''}
        ${e.resource_id && e.resource_type === 'lead' && !e.lead_name ? `<code>${esc(e.resource_id)}</code>` : ''}
      </div>
      ${commandLine}
      <div class="activity-event-meta">
        ${esc(fmtDateTime(e.created_at))}
        ${e.source ? ` · ${esc(e.source)}` : ''}
        ${e.ip ? ` · ${esc(e.ip)}` : ''}
      </div>
      ${detail ? `<pre class="activity-event-detail">${esc(detail)}</pre>` : ''}
    </div>`;
}

function highlightActivityAgentCard() {
  document.querySelectorAll('.activity-agent-card').forEach((card) => {
    card.classList.toggle('selected', card.dataset.agentId === activitySelectedAgent);
  });
}

function renderActivityAgentCards(agents) {
  const wrap = $('activityAgentCards');
  if (!wrap) return;
  if (!agents.length) {
    wrap.innerHTML = '<p style="color:var(--fg-dim);font-size:13px">No agents found.</p>';
    return;
  }
  wrap.innerHTML = agents.map((a) => {
    const online = isRecentlyActive(a.last_active_at);
    const lastActive = a.last_active_at ? relativeTime(a.last_active_at) : 'Never';
    const lastEvent = a.last_event_at ? relativeTime(a.last_event_at) : '—';
    return `
      <div class="activity-agent-card" data-agent-id="${esc(a.id)}" role="button" tabindex="0">
        <div class="name-row">
          <span class="dot" style="background:${esc(a.color || '#a78bfa')}"></span>
          ${esc(a.name)}
          ${online ? '<span class="online-badge">● Online</span>' : ''}
        </div>
        <div class="meta">
          Last active: ${esc(lastActive)}<br>
          Last event: ${esc(lastEvent)} · ${a.events_7d || 0} events (7d)<br>
          ${a.active_sessions ? `${a.active_sessions} active session${a.active_sessions === 1 ? '' : 's'}` : 'No active sessions'}
          ${!a.active ? ' · <span style="color:var(--red)">Inactive account</span>' : ''}
        </div>
      </div>`;
  }).join('');

  wrap.querySelectorAll('.activity-agent-card').forEach((card) => {
    const pick = () => {
      activitySelectedAgent = card.dataset.agentId;
      $('activityAgentFilter').value = activitySelectedAgent;
      highlightActivityAgentCard();
      activityPage = 1;
      loadActivity();
    };
    card.addEventListener('click', pick);
    card.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); pick(); }
    });
  });
  highlightActivityAgentCard();
}

function populateActivityAgentFilter(agents) {
  const sel = $('activityAgentFilter');
  if (!sel) return;
  const current = sel.value || activitySelectedAgent;
  sel.innerHTML = '<option value="">All agents</option>' + agents.map((a) =>
    `<option value="${esc(a.id)}">${esc(a.name)}</option>`
  ).join('');
  sel.value = current || '';
  activitySelectedAgent = sel.value;
}

async function loadActivitySummary() {
  const data = await api('/api/activity/summary');
  activityAgents = data.agents || [];
  populateActivityAgentFilter(activityAgents);
  renderActivityAgentCards(activityAgents);
}

async function loadActivity(append = false) {
  try {
    if (!append) {
      activityPage = 1;
      $('activityList').innerHTML = '';
    }
    const params = new URLSearchParams({ page: String(activityPage) });
    const agentId = $('activityAgentFilter')?.value || '';
    const group = $('activityGroupFilter')?.value || '';
    const action = $('activityActionFilter')?.value?.trim() || '';
    const from = $('activityFromFilter')?.value || '';
    const to = $('activityToFilter')?.value || '';
    if (agentId) params.set('agent_id', agentId);
    if (group && group !== 'all') params.set('group', group);
    if (action) params.set('action', action);
    if (from) params.set('from', from);
    if (to) params.set('to', to);

    const data = await api(`/api/activity?${params}`);
    activityTotal = data.total || 0;
    const events = data.events || [];
    const html = events.map(renderActivityEvent).join('');
    if (append) $('activityList').insertAdjacentHTML('beforeend', html);
    else $('activityList').innerHTML = html || '<p style="color:var(--fg-dim)">No activity for these filters.</p>';
    const loaded = activityPage * (data.limit || 50);
    $('activityLoadMoreBtn')?.classList.toggle('hidden', loaded >= activityTotal || !events.length);
  } catch (e) {
    if (!append) $('activityList').innerHTML = `<p style="color:var(--red);font-size:13px">${esc(e.message)}</p>`;
  }
}

async function loadActivityTab() {
  try {
    $('activityPurgeCard')?.classList.toggle('hidden', me?.role !== 'admin');
    await loadActivitySummary();
    activityPage = 1;
    await loadActivity();
  } catch (e) {
    $('activityAgentCards').innerHTML = `<p style="color:var(--red);font-size:13px">${esc(e.message)}</p>`;
  }
}

function activityPurgeParams() {
  const before = $('activityPurgeBefore')?.value || '';
  if (!before) throw new Error('Choose a date first');
  const params = new URLSearchParams({ before });
  const agentId = $('activityAgentFilter')?.value || '';
  if (agentId) params.set('agent_id', agentId);
  return params;
}

async function previewActivityPurge() {
  const summary = $('activityPurgeSummary');
  if (!summary) return;
  try {
    const params = activityPurgeParams();
    const data = await api(`/api/activity/purge?${params}`);
    const agentName = activityAgents.find((a) => a.id === data.agent_id)?.name;
    const scope = data.agent_id ? ` for <strong>${esc(agentName || data.agent_id)}</strong>` : ' for <strong>all agents</strong>';
    if (!data.count) {
      summary.innerHTML = `No records on or before <strong>${esc(data.before)}</strong>${scope}.`;
      return;
    }
    summary.innerHTML = `
      <strong>${data.count.toLocaleString()}</strong> record${data.count === 1 ? '' : 's'} on or before
      <strong>${esc(data.before)}</strong>${scope}.
      ${data.oldest && data.newest ? `<br>Date range in selection: ${esc(fmtDateTime(data.oldest))} → ${esc(fmtDateTime(data.newest))}` : ''}`;
  } catch (e) {
    summary.innerHTML = `<span style="color:var(--red)">${esc(e.message)}</span>`;
  }
}

async function runActivityPurge() {
  try {
    const params = activityPurgeParams();
    const preview = await api(`/api/activity/purge?${params}`);
    if (!preview.count) {
      showToast('No records to delete for that date', 'warning');
      await previewActivityPurge();
      return;
    }
    const agentName = activityAgents.find((a) => a.id === preview.agent_id)?.name;
    const scope = preview.agent_id ? ` for ${agentName || preview.agent_id}` : ' for all agents';
    if (!confirm(`Delete ${preview.count.toLocaleString()} activity record${preview.count === 1 ? '' : 's'} on or before ${preview.before}${scope}? This cannot be undone.`)) {
      return;
    }
    const result = await api(`/api/activity/purge?${params}`, { method: 'DELETE' });
    showToast(`Deleted ${result.deleted.toLocaleString()} record${result.deleted === 1 ? '' : 's'}`, 'success');
    $('activityPurgeSummary').innerHTML = `<span style="color:var(--green)">Deleted ${result.deleted.toLocaleString()} record${result.deleted === 1 ? '' : 's'} on or before ${esc(result.before)}.</span>`;
    await loadActivitySummary();
    activityPage = 1;
    await loadActivity();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

function parseImportPayload() {
  const raw = $('importJson').value.trim();
  if (!raw) throw new Error('Paste JSON first');
  const parsed = JSON.parse(raw);
  if (Array.isArray(parsed)) return parsed;
  if (parsed && Array.isArray(parsed.leads)) return parsed.leads;
  throw new Error('Expected a JSON array of leads');
}

async function runImport(dryRun) {
  try {
    const leads = parseImportPayload();
    const result = await api('/api/leads/import', {
      method: 'POST',
      body: { dry_run: dryRun, leads },
    });
    $('importResult').textContent = JSON.stringify(result, null, 2);
    showToast(dryRun ? 'Dry run complete' : `Imported ${result.imported}`, dryRun ? '' : 'success');
  } catch (e) {
    showToast(e.message, 'error');
    $('importResult').textContent = e.message;
  }
}

async function changePassword() {
  const next = $('newPassMe').value;
  if (next.length < 8) {
    showToast('New password must be at least 8 characters', 'error');
    return;
  }
  try {
    await api('/api/me/password', {
      method: 'POST',
      body: {
        current_password: $('curPass').value,
        new_password: next,
      },
    });
    showToast('Password updated', 'success');
    resetPasswordInput($('curPass'));
    resetPasswordInput($('newPassMe'));
  } catch (e) {
    showToast(e.message, 'error');
  }
}

async function loadDuplicateScan() {
  const summary = $('dupScanSummary');
  const wrap = $('dupScanGroups');
  if (!wrap) return;
  summary.textContent = 'Scanning…';
  wrap.innerHTML = '';
  try {
    const data = await api('/api/duplicates/scan');
    summary.textContent = data.group_count
      ? `${data.group_count} duplicate group${data.group_count === 1 ? '' : 's'} · ${data.duplicate_lead_count} inquiries involved`
      : 'No duplicate groups found.';
    if (!data.groups?.length) return;
    wrap.innerHTML = data.groups.map((g, gi) => `
      <div class="dup-scan-group" data-group="${gi}">
        <h3>${g.count} matching inquiries</h3>
        ${g.leads.map((l) => `
          <div class="dup-scan-lead">${esc(l.name)} · ${esc(l.phone || '—')} · ${esc(l.type)} · ${esc(l.status_label || l.status)}
            ${l.email ? ' · ' + esc(l.email) : ''} · matched: ${esc((l.match_reasons || []).join(', '))}</div>`
        ).join('')}
        <div style="margin-top:10px;display:flex;gap:8px;flex-wrap:wrap;align-items:center">
          <label style="font-size:12px">Keep primary:
            <select class="form-select dup-primary-select" data-group="${gi}" style="display:inline-block;width:auto;min-width:220px;margin-left:6px">
              ${g.leads.map((l) => `<option value="${esc(l.id)}">${esc(l.name)} (${esc(l.inquiry_date || '')})</option>`).join('')}
            </select>
          </label>
          <button type="button" class="btn btn-primary btn-sm dup-merge-btn" data-group="${gi}">Merge group</button>
          <a class="btn btn-ghost btn-sm" href="/leads/?q=${encodeURIComponent(g.leads[0]?.phone || g.leads[0]?.name || '')}&amp;search_scope=global" target="_blank" rel="noopener">Open in All Leads</a>
        </div>
      </div>`).join('');

    wrap.querySelectorAll('.dup-merge-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const gi = Number(btn.dataset.group);
        const group = data.groups[gi];
        const primaryId = wrap.querySelector(`.dup-primary-select[data-group="${gi}"]`)?.value;
        const secondaryIds = group.leads.map((l) => l.id).filter((id) => id !== primaryId);
        if (!primaryId || !secondaryIds.length) return;
        const ok = confirm(`Merge ${secondaryIds.length} inquiry(s) into the primary record?`);
        if (!ok) return;
        try {
          btn.disabled = true;
          await api('/api/leads/merge', { method: 'POST', body: { primary_id: primaryId, secondary_ids: secondaryIds } });
          showToast('Group merged', 'success');
          await loadDuplicateScan();
        } catch (e) {
          showToast(e.message || 'Merge failed', 'error');
          btn.disabled = false;
        }
      });
    });
  } catch (e) {
    summary.textContent = '';
    wrap.innerHTML = `<p style="color:var(--red)">${esc(e.message || 'Scan failed')}</p>`;
  }
}

function currentTargetsMonth() {
  const el = $('targetsMonth');
  if (el?.value) return el.value;
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Colombo' }).slice(0, 7);
}

async function loadTargets() {
  const month = currentTargetsMonth();
  if ($('targetsMonth') && !$('targetsMonth').value) $('targetsMonth').value = month;
  const wrap = $('targetsList');
  if (!wrap) return;
  wrap.innerHTML = '<p style="color:var(--fg-dim)">Loading…</p>';
  try {
    const data = await api('/api/agent-targets?month=' + encodeURIComponent(month));
    if (!data.agents?.length) {
      wrap.innerHTML = '<p style="color:var(--fg-dim)">No working agents found.</p>';
      return;
    }
    wrap.innerHTML = `
      <div class="table-wrap">
        <table class="leads-table targets-table">
          <thead>
            <tr>
              <th>Agent</th>
              <th>Profit (LKR)</th>
              <th>Bookings</th>
              <th>Tours</th>
              <th>Visas</th>
              <th>Tickets</th>
            </tr>
          </thead>
          <tbody>
            ${data.agents.map((a) => `
              <tr data-agent-id="${esc(a.agent_id)}">
                <td><span class="agent-dot" style="background:${esc(a.color || '#00AEEF')}"></span> ${esc(a.name)}</td>
                <td><input type="number" class="form-input target-input" data-field="target_profit" min="0" step="1000" value="${a.target_profit || 0}"></td>
                <td><input type="number" class="form-input target-input" data-field="target_converted" min="0" step="1" value="${a.target_converted || 0}"></td>
                <td><input type="number" class="form-input target-input" data-field="target_tours" min="0" step="1" value="${a.target_tours || 0}"></td>
                <td><input type="number" class="form-input target-input" data-field="target_visas" min="0" step="1" value="${a.target_visas || 0}"></td>
                <td><input type="number" class="form-input target-input" data-field="target_tickets" min="0" step="1" value="${a.target_tickets || 0}"></td>
              </tr>`).join('')}
          </tbody>
        </table>
      </div>`;
  } catch (e) {
    wrap.innerHTML = `<p style="color:var(--red)">${esc(e.message || 'Failed to load targets')}</p>`;
  }
}

async function saveTargets() {
  const month = currentTargetsMonth();
  const rows = [...document.querySelectorAll('#targetsList tr[data-agent-id]')];
  const targets = rows.map((row) => {
    const entry = { agent_id: row.dataset.agentId };
    row.querySelectorAll('.target-input').forEach((input) => {
      entry[input.dataset.field] = Number(input.value || 0);
    });
    return entry;
  });
  try {
    $('saveTargetsBtn').disabled = true;
    await api('/api/agent-targets', { method: 'PUT', body: { month, targets } });
    showToast('Targets saved for ' + month, 'success');
    await loadTargets();
  } catch (e) {
    showToast(e.message || 'Save failed', 'error');
  } finally {
    $('saveTargetsBtn').disabled = false;
  }
}

boot();
